import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './App.css';
import GameContent from "./components/GameContent";
import RulesModal from "./components/Modals/RulesModal/RulesModal";
import FieldSelectMenu from "./components/FieldSelectMenu";
import HotSeatPlayersInitPage from "./components/HotSeatPlayersInitPage/HotSeatPlayersInitPage";
import BackgroundMusic from "./components/BackgroundMusic/BackgroundMusic";
import UserNamePage from "./components/UserNamePage/UserNamePage";
import YandexAuthPage from "./components/YandexAuthPage/YandexAuthPage";
import PasswordResetPage from "./components/YandexAuthPage/PasswordResetPage";
import {GUEST_PROFILE} from './configs/guestProfile';
import {sendAppEvent, sendNetworkEvent} from './analytics/gameAnalytics';
import {getDurationBucket} from './utils/analytics/getDurationBucket';
import {checkIfIntroSlidesShown, markIntroSlidesShown} from './utils/intro/introSlidesShownStorage';
import {markIntroSlidesSeen} from './utils/intro/markIntroSlidesSeen';
// AUDIT_LOGIN_TEMP: временная страница входа для аудиторской проверки
import AuditLoginPage from "./components/AuditLoginPage/AuditLoginPage";
import {AUDIT_LOGIN_ROUTE_PATH} from "./constants/auditLogin";
import {checkIfReferalPath} from "./utils/partner/checkIfReferalPath";
import {checkIfMatchSessionShouldReset} from "./utils/screens/checkIfMatchSessionShouldReset";
import {buildExtendedResultsFromMatch} from "./utils/replay/buildExtendedResultsFromMatch";
import {prepareTutorialReplayActions} from "./utils/replay/prepareTutorialReplayActions";
import MainMenu from "./components/MainMenu/MainMenu";
import LegalConsentGate from "./components/pages/LegalConsentGate/LegalConsentGate";
import {checkIfLegalConsentRequired} from "./utils/legal/checkIfLegalConsentRequired";
import LobbyListPage from "./components/LobbyListPage/LobbyListPage";
import LobbyPage from "./components/LobbyPage/LobbyPage";
import RankMatchWaiting from "./components/RankMatchWaiting/RankMatchWaiting";
import RankMatchSetup from "./components/RankMatchSetup/RankMatchSetup";
import ConnectionLostOverlay from "./components/ConnectionLostOverlay/ConnectionLostOverlay";
import OrientationLockOverlay from "./components/OrientationLockOverlay/OrientationLockOverlay";
import VersionMismatchOverlay from "./components/VersionMismatchOverlay/VersionMismatchOverlay";
import GameEndedByHostOverlay from "./components/GameEndedByHostOverlay/GameEndedByHostOverlay";
import CampfireSparks from './components/BackgroundEffects/CampfireSparks';
import AppSplash from './components/AppSplash/AppSplash';
import NotEnoughFactionsModal from "./components/Modals/NotEnoughFactionsModal/NotEnoughFactionsModal";
import LobbyIsFullModal from "./components/Modals/LobbyIsFullModal/LobbyIsFullModal";
import MatchReplayUnavailableModal from "./components/Modals/MatchReplayUnavailableModal/MatchReplayUnavailableModal";
import RankMatchSearchNoticeModal, {
  RANK_MATCH_SEARCH_NOTICES
} from "./components/Modals/RankMatchSearchNoticeModal/RankMatchSearchNoticeModal";
import {RANK_MATCH_JOIN_OUTCOMES, requestRankMatchJoin} from './utils/lobby/requestRankMatchJoin';
import {GAME_FIELDS} from './configs/fields';
import {
  NEWBIE_BATTLE_FIELD_ID,
  NEWBIE_BATTLE_ROUND_COUNT,
} from './configs/newbieBattle';
import {buildNewbieBattlePlayers} from './utils/localMatch/buildNewbieBattlePlayers';
import {resendLocalMatchResultsOnAuth} from './utils/localMatch/applyLocalMatchSaveResult';
import {createOwnLobby} from './utils/lobby/createOwnLobby';
import {
  missionEditorStart,
  resetState,
  setAvailableFactions,
  setGameType,
  setHideModalsExceptGameEnd,
  setPlayerAuth,
  setServerTimeDelta,
  showConstructionPhaseInfoModal,
  showToast,
} from "./redux/actions";
import {GAME_TYPES} from "./constants/game";
import {prepareStoreForLocalGame} from "./utils/gameState/prepareStoreForLocalGame";
import {checkIfAllPlayersAreBots} from "./utils/gameState/checkIfAllPlayersAreBots";
import store from './redux/store';
import {
  removeConnectionLostHandler,
  removeReconnectStatusHandler,
  removeSessionSupersededHandler,
  setConnectionLostHandler,
  setReconnectStatusHandler,
  setSessionSupersededHandler
} from './utils/connectionMonitor';
import {closeNetworkSocket} from './utils/networkSocket';

import {API_BASE} from './utils/apiConfig';
import {
  AUTH_CHECK_TIMEOUT_MS,
  RequestTimeoutError,
  checkIfNetworkFailure,
  fetchWithTimeout,
} from './utils/network/startupConnectivity';
import {SCREENS} from "./constants/screens";
import {INVENTORY_FOCUS_SECTIONS} from './constants/inventoryFocusSections';
import {prepareLocalMatchPlayers} from './utils/players/prepareLocalMatchPlayers';
import InventoryPage from "./components/pages/InventoryPage/InventoryPage";
import GloryHallPage from "./components/pages/GloryHallPage/GloryHallPage";
import BestCollectionsPage from './components/pages/BestCollectionsPage/BestCollectionsPage';
import TutorialMissionsPage from "./components/pages/TutorialMissionsPage/TutorialMissionsPage";
import QuestsPage from './components/pages/QuestsPage/QuestsPage';
import CampaignMissionsPage from './components/pages/CampaignMissionsPage/CampaignMissionsPage';
import RatingCrystalsPage from './components/pages/RatingCrystalsPage/RatingCrystalsPage';
import SettingsPage from "./components/pages/SettingsPage/SettingsPage";
import AdminPage from "./components/pages/AdminPage/AdminPage";
import AdminModalsTestPage from "./components/pages/AdminModalsTestPage/AdminModalsTestPage";
import AdminGameUiKitPage from "./components/pages/AdminGameUiKitPage/AdminGameUiKitPage";
import NetworkErrorPage from "./components/NetworkErrorPage/NetworkErrorPage";
import IntroSlides from "./components/IntroSlides/IntroSlides";
import {trackAdGoal, AD_GOALS} from "./analytics/adTracking";
import AdminUsersPage from "./components/pages/AdminUsersPage/AdminUsersPage";
import AdminDeleteUserPage from "./components/pages/AdminDeleteUserPage/AdminDeleteUserPage";
import AdminUserProfilePage from "./components/pages/AdminUserProfilePage/AdminUserProfilePage";
import GrantChestPage from "./components/pages/GrantChestPage/GrantChestPage";
import ChangeNicknamePage from "./components/pages/ChangeNicknamePage/ChangeNicknamePage";
import GrantAssetsPage from "./components/pages/GrantAssetsPage/GrantAssetsPage";
import GrantSkinPage from "./components/pages/GrantSkinPage/GrantSkinPage";
import GrantCrystalsPage from "./components/pages/GrantCrystalsPage/GrantCrystalsPage";
import GrantPremiumPage from "./components/pages/GrantPremiumPage/GrantPremiumPage";
import AllSkinsPage from "./components/pages/AllSkinsPage/AllSkinsPage";
import MatchHistoryPage from "./components/pages/MatchHistoryPage/MatchHistoryPage";
import PurchaseHistoryPage from "./components/pages/PurchaseHistoryPage/PurchaseHistoryPage";
import ChestOpeningHistoryPage from "./components/pages/ChestOpeningHistoryPage/ChestOpeningHistoryPage";
import DailyLoginRewardHistoryPage from "./components/pages/DailyLoginRewardHistoryPage/DailyLoginRewardHistoryPage";
import ShopPage, {TABS as SHOP_TABS} from "./components/pages/ShopPage/ShopPage";
import MatchDetailsPage from "./components/pages/MatchDetailsPage/MatchDetailsPage";
import AcknowledgmentsPage from "./components/pages/AcknowledgmentsPage/AcknowledgmentsPage";
import LicenseAgreementPage from "./components/pages/LicenseAgreementPage/LicenseAgreementPage";
import PrivacyPolicyPage from "./components/pages/PrivacyPolicyPage/PrivacyPolicyPage";
import ContactsPage from "./components/pages/ContactsPage/ContactsPage";
import FactionPage from "./components/pages/FactionPage/FactionPage";
import PartnerFormPage from "./components/pages/PartnerFormPage/PartnerFormPage";
import PartnerRegistrationsPage from "./components/pages/PartnerRegistrationsPage/PartnerRegistrationsPage";
import AdminPartnersPage from "./components/pages/AdminPartnersPage/AdminPartnersPage";
import FriendInvitePage from "./components/pages/FriendInvitePage/FriendInvitePage";
import AdminFriendReferalConfigPage from "./components/pages/AdminFriendReferalConfigPage/AdminFriendReferalConfigPage";
import AdminLoginPromoPage from "./components/pages/AdminLoginPromoPage/AdminLoginPromoPage";
import {
  FRIEND_REFERAL_STORAGE_KEY,
  FRIEND_REFERAL_URL_PREFIX,
  PARTNER_REFERAL_STORAGE_KEY,
  PARTNER_REFERAL_URL_PREFIX,
} from "./constants/partner";
import {EMAIL_CONFIRM_URL_PREFIX, PASSWORD_RESET_URL_PREFIX} from "./constants/auth";
import {LOBBY_INVITE_STORAGE_KEY, parseInviteLobbyIdFromPath} from './utils/lobby/lobbyInviteUrl';
import {
  INVITED_LOBBY_JOIN_OUTCOMES,
  INVITED_LOBBY_NETWORK_ERROR_MESSAGE,
  INVITED_LOBBY_UNAVAILABLE_MESSAGE,
  RANK_MATCH_JOIN_NOT_ALLOWED_MESSAGE,
  RANK_MATCH_PHASE_STARTED_MESSAGE,
  resolveInvitedLobbyJoinOutcome,
} from './utils/lobby/resolveInvitedLobbyJoinOutcome';
import {
  AI_LOBBY_JOIN_NETWORK_ERROR,
  getAiLobbyJoinErrorText,
} from './utils/aiMatch/getAiLobbyJoinErrorText';
import AiMatchJoinErrorModal from './components/Modals/AiMatchPromoModal/AiMatchJoinErrorModal';
import {addClassNameIfTrue} from "./utils/other/addClassNameIfTrue";
import {useOpaqueOnMount} from "./hooks/visuals/useOpaqueOnMount";
import {MENU_SOUND_MANAGER} from "./scenes/MenuSoundManager";
import {selectGameResults} from "./redux/reducers/sharedStateReducer/selectors";

// Сервер ответил 5xx на проверку авторизации: приложению стартовать не с чем,
// поэтому обрабатываем это как отсутствие связи
class NetworkUnavailableError extends Error {}

function App() {
  const dispatch = useDispatch();
  const [screen, setScreen] = useState(SCREENS.AUTH);
  // AUDIT_LOGIN_TEMP: адрес считывается ОДИН раз при монтировании — дальше на
  // window.location полагаться нельзя, его чистят эффекты реферальных ссылок
  // и редиректы авторизации. Инструкция по удалению — server/config/auditLogin.js
  const [isAuditLoginRoute] = useState(() => window.location.pathname === AUDIT_LOGIN_ROUTE_PATH);
  const [selectedField, setSelectedField] = useState(null);
  const [gameRoundCount, setGameRoundCount] = useState(null);
  const [players, setPlayers] = useState(null);
  // Режим зрителя локальной партии: только явная галочка на
  // HotSeatPlayersInitPage (партия «все боты» без нее играется с обычным UI)
  const [isSpectatorGame, setIsSpectatorGame] = useState(false);
  // Таймер хода локальной партии: тоже только явная галочка на
  // HotSeatPlayersInitPage, по умолчанию выключен
  const [isLocalTimerEnabled, setIsLocalTimerEnabled] = useState(false);
  // Подложка поля локальной партии, выбранная игроком на HotSeatPlayersInitPage:
  // идентификатор подложки или null — «Случайная»
  const [localGroundBackground, setLocalGroundBackground] = useState(null);
  const [enteredLobbyId, setEnteredLobbyId] = useState(null);
  const [connectionLost, setConnectionLost] = useState(false);
  const [reconnectStatus, setReconnectStatus] = useState(null);
  const [sessionSuperseded, setSessionSuperseded] = useState(false);
  const [versionMismatch, setVersionMismatch] = useState(false);
  const [versionMismatchServerVersion, setVersionMismatchServerVersion] = useState(null);
  const [gameEndedByHost, setGameEndedByHost] = useState(false);
  const [gameInteractionDisabled, setGameInteractionDisabled] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  // Токен смены пароля из ссылки письма. Читается из адреса ОДИН раз: дальше
  // на строку адреса полагаться нельзя, ее чистят редиректы авторизации
  const [passwordResetToken, setPasswordResetToken] = useState(() => {
    const {pathname} = window.location;
    if (!pathname.startsWith(PASSWORD_RESET_URL_PREFIX)) {
      return '';
    }
    return decodeURIComponent(
      pathname.slice(PASSWORD_RESET_URL_PREFIX.length).split('/')[0] || ''
    ).trim();
  });
  const [adminSelectedUser, setAdminSelectedUser] = useState(null);
  const [glorySelectedUser, setGlorySelectedUser] = useState(null);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [matchDetailsContext, setMatchDetailsContext] = useState(null);
  const [matchHistoryContext, setMatchHistoryContext] = useState(null);
  const [purchaseHistoryContext, setPurchaseHistoryContext] = useState(null);
  const [chestOpeningHistoryContext, setChestOpeningHistoryContext] = useState(null);
  const [dailyLoginRewardHistoryContext, setDailyLoginRewardHistoryContext] = useState(null);
  const [changeNicknameContext, setChangeNicknameContext] = useState(null);
  const [grantChestContext, setGrantChestContext] = useState(null);
  const [grantAssetsContext, setGrantAssetsContext] = useState(null);
  const [grantSkinContext, setGrantSkinContext] = useState(null);
  const [grantCrystalsContext, setGrantCrystalsContext] = useState(null);
  const [grantPremiumContext, setGrantPremiumContext] = useState(null);
  const [factionPageContext, setFactionPageContext] = useState(null);
  // Партнерская программа: контекст формы партнера ({userId, userName,
  // isSelfView, returnScreen}) и истории регистраций
  const [partnerFormContext, setPartnerFormContext] = useState(null);
  const [partnerRegistrationsContext, setPartnerRegistrationsContext] = useState(null);
  // «Пригласи друга»: {userId, userName, isAdminView, returnScreen}
  const [friendInviteContext, setFriendInviteContext] = useState(null);
  // Вкладка, с которой открыть магазин (напр. переход «за кристаллами» из
  // модалок обмена/нехватки кристаллов вне самого ShopPage) — null = обычная
  // вкладка по умолчанию (сундуки)
  const [shopInitialTab, setShopInitialTab] = useState(null);
  // Секция своей коллекции, к которой ее прокрутить при открытии (см.
  // INVENTORY_FOCUS_SECTIONS) — null = обычное открытие сверху. Живет, пока
  // открыта коллекция: возврат в нее с других страниц к секции не ведет
  const [inventoryFocusSection, setInventoryFocusSection] = useState(null);
  const [allSkinsContext, setAllSkinsContext] = useState(null);
  const [notEnoughFactionsModal, setNotEnoughFactionsModal] = useState(null);
  // Отказ входа в заполненное лобби (ответ сервера LOBBY_IS_FULL): и из
  // списка лобби, и при входе по ссылке-приглашению
  const [lobbyIsFullModal, setLobbyIsFullModal] = useState(null);
  // Сообщение, с которым открывается список лобби (неудачное приглашение)
  const [lobbyListInitialError, setLobbyListInitialError] = useState(null);
  // Ограничение матч-мейкинга по числу противников из профиля (rankMatch.js) —
  // приходит в ответе rank-match/join, передается экрану ожидания поиска
  const [rankMatchOpponentCounts, setRankMatchOpponentCounts] = useState(null);
  // Идентификатор текущего поиска из ответа rank-match/join: отмена поиска
  // шлет его, чтобы не удалить поиск, перехваченный другой вкладкой
  const [rankMatchSearchId, setRankMatchSearchId] = useState(null);
  // Почему игрок не на экране поиска (RANK_MATCH_SEARCH_NOTICES) или null
  const [rankMatchSearchNotice, setRankMatchSearchNotice] = useState(null);
  // Запрос входа в поиск уже в пути: повторные нажатия его не дублируют
  const isRankMatchJoinInFlightRef = useRef(false);
  // «Битва с AI Claude Opus»: текст отказа входа в лобби дня (модалка поверх
  // меню) и защита от повторных нажатий, пока запрос входа в пути
  const [aiLobbyJoinErrorText, setAiLobbyJoinErrorText] = useState(null);
  const isAiPromoJoinInFlightRef = useRef(false);
  const [replayActions, setReplayActions] = useState(null);
  // Награды и рейтинг для СТАРЫХ записей, где их нет в самой записи
  const [replayFallbackResults, setReplayFallbackResults] = useState(null);
  // Смотрится запись прохождения обучения (не сетевого матча)
  const [isTutorialReplay, setIsTutorialReplay] = useState(false);
  const [isReplayUnavailableModalVisible, setIsReplayUnavailableModalVisible] = useState(false);
  // Запись есть, но сервер отказал этому зрителю (а не «записи нет»)
  const [isReplayAccessDenied, setIsReplayAccessDenied] = useState(false);
  const [userProfileReturnScreen, setUserProfileReturnScreen] = useState(SCREENS.GLORY_HALL);

  // Открыть публичный профиль игрока с запоминанием экрана возврата. Один
  // путь на всех: Зал славы, список приглашенных друзей, список регистраций
  // партнера — профиль везде один и тот же (AdminUserProfilePage isPublicView)
  const openUserProfile = useCallback((userId, userName, returnScreen) => {
    setGlorySelectedUser({id: userId, name: userName});
    setUserProfileReturnScreen(returnScreen);
    setScreen(SCREENS.USER_PROFILE);
  }, []);
  const [devLogin, setDevLogin] = useState(false);
  // Старт без сети: проверка авторизации не дошла до сервера. Показываем
  // экран «нет связи» вместо страницы входа — у игрока сессия, скорее всего,
  // есть, и предлагать ему войти заново неправильно
  const [isStartupNetworkError, setIsStartupNetworkError] = useState(false);
  // Вступительная колода показывается ровно один раз на устройство — тому, кто
  // видит игру впервые (отметка живет в localStorage, авторизации еще нет).
  // Решение принимается один раз при старте: сама колода ставит отметку на
  // своем монтировании, и без этого снимка она бы тут же исчезла с экрана
  // Вступительная колода: показывается тому, кто ее еще не закрывал.
  // Отметка живет в двух местах — в профиле (чтобы не показать заново с
  // другого устройства) и в localStorage (у гостя профиля еще нет)
  const [isIntroSlidesPending, setIsIntroSlidesPending] = useState(() => !checkIfIntroSlidesShown());
  const authChecked = useRef(false);
  const auth = useSelector((state) => state.private.auth);

  // Актуальный screen для visibilitychange-обработчика ниже, который
  // подписывается один раз при монтировании и не должен пересоздаваться
  // на каждую смену экрана
  const screenRef = useRef(screen);
  useEffect(() => {
    screenRef.current = screen;
  }, [screen]);

  const isOpaque = useOpaqueOnMount();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen]);

  useEffect(() => {
    if (screen !== SCREENS.INVENTORY) {
      setInventoryFocusSection(null);
    }
  }, [screen]);

  useEffect(() => {
    MENU_SOUND_MANAGER.preload();
  }, []);

  // Открытие сайта по реферальной ссылке — партнерской
  // ({домен}/referal/partner/{partnerReferalId}) или дружеской
  // ({домен}/referal/friend/{userId}): запоминаем идентификатор в
  // localStorage — его подхватит экран ввода никнейма при первичной
  // регистрации (см. UserNamePage). На сам URL дальше не полагаемся:
  // редиректы авторизации Яндекса его собьют.
  //
  // Адрес здесь НЕ обрезается: ссылки часто открывают во встроенном браузере
  // мессенджера (Telegram, VK и т.п.), а оттуда переходят в обычный браузер
  // кнопкой «открыть в браузере» — она открывает ТЕКУЩИЙ адрес. Обрезав его
  // сразу, мы отправляли пользователя в новый браузер по голому домену: там
  // свой localStorage, метки в нем нет, и привязка реферала терялась.
  // Обрезаем позже — когда метка уже отработала (см. finishAuth)
  useEffect(() => {
    const {pathname} = window.location;
    const referalPrefixes = [
      {prefix: PARTNER_REFERAL_URL_PREFIX, storageKey: PARTNER_REFERAL_STORAGE_KEY},
      {prefix: FRIEND_REFERAL_URL_PREFIX, storageKey: FRIEND_REFERAL_STORAGE_KEY},
    ];
    const matched = referalPrefixes.find(({prefix}) => pathname.startsWith(prefix));
    if (!matched) return;
    const referalId = decodeURIComponent(
      pathname.slice(matched.prefix.length).split('/')[0] || ''
    ).trim();
    if (referalId) {
      localStorage.setItem(matched.storageKey, referalId);
    }
    // Приглашение в лобби — дружеская реферальная ссылка с хвостом
    // /lobby/{lobbyId} (см. buildLobbyInviteUrl): реферальную часть разбор
    // выше уже забрал, здесь забираем лобби. Запоминаем ДО очистки URL —
    // дальше на адрес полагаться нельзя, его собьют редиректы авторизации
    const inviteLobbyId = parseInviteLobbyIdFromPath(pathname);
    if (inviteLobbyId) {
      sessionStorage.setItem(LOBBY_INVITE_STORAGE_KEY, inviteLobbyId);
    }
  }, []);

  const fetchAndSetAvailableFactions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/available-factions`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch available factions');
      const data = await res.json();
      dispatch(setAvailableFactions(data.factions || []));
    } catch (e) {
      console.error('Fetch available factions error', e);
      dispatch(setAvailableFactions([]));
    }
  }, [dispatch]);

  // Вход по почте: ответ той же формы, что у обмена кода Яндекса, поэтому и
  // разбирается так же — новому игроку сначала ник, остальным сразу в меню
  const handleEmailAuthSuccess = useCallback((data) => {
    if (data?.needsOnboarding) {
      // Экран выбора ника живет на AUTH: гость входит из модалки, находясь в
      // меню, и без переключения экрана ник спросить было бы негде
      setNeedsOnboarding(true);
      setScreen(SCREENS.AUTH);
      return;
    }
    if (data?.user) {
      finishAuthRef.current(data.user);
    }
  }, []);

  const finishAuth = useCallback((user) => {
    localStorage.setItem('playerName', user.name);
    localStorage.setItem('playerId', user.id);
    // Вход в аккаунт — любой успешной авторизацией, включая автоматическую по
    // сохраненной сессии. Регистрацию отдельной целью шлет UserNamePage
    trackAdGoal(AD_GOALS.LOGIN, {userId: user.id});
    dispatch(setPlayerAuth(user));
    fetchAndSetAvailableFactions();
    setScreen(SCREENS.MENU);
    // Итоги локальных партий, не дошедшие до сервера в прошлый раз (страница
    // закрылась или перезагрузилась, пока шли повторы), досылаем сейчас
    resendLocalMatchResultsOnAuth(user.id, dispatch);
    // Метка уже в localStorage, а у новых игроков уже применена сервером при
    // регистрации — дальше реферальный адрес в строке не нужен. Чистим только
    // теперь (а не на старте, см. эффект разбора ссылки), чтобы переход из
    // встроенного браузера мессенджера в обычный уносил ссылку целиком
    if (checkIfReferalPath(window.location.pathname)) {
      window.history.replaceState(null, '', '/');
    }
  }, [dispatch, fetchAndSetAvailableFactions]);

  // finishAuth объявлен ниже обработчика входа по почте — ссылка избавляет от
  // перестановки объявлений в и без того длинном компоненте
  const finishAuthRef = useRef(finishAuth);
  finishAuthRef.current = finishAuth;

  // Вынесено сюда (а не внутрь auth-check эффекта ниже), чтобы его же можно
  // было повторно дернуть при возврате вкладки из фона (см.
  // handleVisibilityChange) — на мобильных первый вызов сразу после
  // разблокировки телефона иногда проваливается из-за еще не восстановившейся
  // сети, а эффект инициализации запускается ровно один раз и сам по себе
  // не переспрашивает
  const checkMe = useCallback(async () => {
    try {
      // Тайм-аут обязателен: «висящая» сеть (VPN без связи, блокировка
      // адреса) держит запрос минутами, и сплеш крутился бы без конца
      const res = await fetchWithTimeout(`${API_BASE}/auth/me`, {
        method: 'GET',
        credentials: 'include',
      }, AUTH_CHECK_TIMEOUT_MS);

      // 401/403 — обычное «не авторизован», дальше страница входа. А вот 5xx
      // означает, что приложение просто не смогло подняться: сервер ответил,
      // но ответить по делу не смог — это тот же случай, что и обрыв сети
      if (res.status >= 500) {
        throw new NetworkUnavailableError();
      }
      // Сервер ответил по делу — экран «нет связи» с прошлой попытки больше
      // не нужен (иначе он перекрыл бы и выбор ника, и вход на экране AUTH)
      setIsStartupNetworkError(false);
      if (!res.ok) throw new Error('Not authenticated');

      const data = await res.json();
      if (data.needsOnboarding) {
        // Аккаунт есть, а ник так и не выбран: в прошлый раз человек закрыл
        // вкладку на этом шаге (плохая связь после редиректа, письмо открыто
        // мимоходом). Спрашиваем снова, иначе он навсегда останется с
        // подставным именем, которого сам не выбирал
        sendAppEvent('auth_check', 'onboarding');
        setNeedsOnboarding(true);
        setScreen(SCREENS.AUTH);
        return;
      }
      if (data.user) {
        sendAppEvent('auth_check', 'ok');
        finishAuth(data.user);
      }
    } catch (e) {
      // fetch падает исключением, только когда запрос не дошел до сервера
      // (нет сети, сервер недоступен) или не дождался ответа — в этом случае
      // показываем экран «нет связи», а не страницу входа
      if (e instanceof NetworkUnavailableError || checkIfNetworkFailure(e)) {
        console.error('Auth check network error', e);
        sendAppEvent('auth_check', e instanceof RequestTimeoutError ? 'network_timeout' : 'network_error');
        setIsStartupNetworkError(true);
        return;
      }

      // Не авторизован — это НЕ повод показывать страницу входа: человек,
      // который видит игру впервые, попадает сразу в главное меню с гостевым
      // профилем (см. configs/guestProfile.js), а войти ему предложат кнопки,
      // которым аккаунт действительно нужен. Исключение — служебный адрес
      // входа для аудита: там нужна именно его страница
      if (!isAuditLoginRoute) {
        sendAppEvent('auth_check', 'guest');
        dispatch(setPlayerAuth(GUEST_PROFILE));
        setScreen(SCREENS.MENU);
      }
    } finally {
      setIsAuthLoading(false);
    }
  }, [finishAuth, dispatch, isAuditLoginRoute]);

  // Повтор проверки авторизации со сплешем — при возврате вкладки из фона и
  // когда экран «нет связи» увидел, что сервер снова отвечает
  const retryAuthCheck = useCallback(() => {
    setIsAuthLoading(true);
    checkMe();
  }, [checkMe]);

  useEffect(() => {
    const fetchServerTime = async () => {
      try {
        const res = await fetch(`${API_BASE}/time`, {
          method: 'GET',
          headers: {Accept: 'application/json'},
          cache: 'no-store',
        });
        if (!res.ok) return;
        const {timestamp: serverTimestamp} = await res.json();
        const delta = serverTimestamp - Date.now();
        dispatch(setServerTimeDelta(delta));
      } catch (e) {
        console.error('Failed to fetch server time', e);
      }
    };
    fetchServerTime();

    // Возвращение вкладки из фона: за время приостановки калибровка серверного
    // времени могла устареть (буферизованные сообщения смены хода обрабатываются
    // с опозданием) — пересинхронизируемся, чтобы таймер хода не показывал мусор.
    // Заодно: если разблокировка телефона застала первую проверку /auth/me
    // без сети и она молча провалилась (эффект инициализации запускается
    // ровно один раз и сам не переспрашивает), пользователь застревает на
    // экране входа, хотя кука все еще валидна — тихо повторяем проверку
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchServerTime();
        if (screenRef.current === SCREENS.AUTH) {
          retryAuthCheck();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, retryAuthCheck]);

  const handleDevLogin = useCallback(() => {
    const savedName = localStorage.getItem('playerName');
    const savedId = localStorage.getItem('playerId');
    if (savedName && savedId) {
      finishAuth({name: savedName, id: savedId});
    } else {
      // Экран ввода никнейма живет на AUTH — гостя, нажавшего dev-вход прямо
      // из меню, нужно туда вернуть
      setDevLogin(true);
      setScreen(SCREENS.AUTH);
    }
  }, [finishAuth]);

  useEffect(() => {
    if (authChecked.current) return;
    authChecked.current = true;

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    const exchangeCode = async () => {
      try {
        const res = await fetchWithTimeout(`${API_BASE}/auth/yandex/exchange`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          credentials: 'include',
          body: JSON.stringify({code, state}),
        }, AUTH_CHECK_TIMEOUT_MS);

        if (!res.ok) throw new Error('Yandex auth exchange failed');

        const data = await res.json();
        if (data.needsOnboarding) {
          // Новый игрок вернулся от Яндекса и пошел придумывать никнейм —
          // ключевая точка воронки регистрации
          sendAppEvent('oauth_exchange', 'onboarding');
          setNeedsOnboarding(true);
        } else if (data.user) {
          sendAppEvent('oauth_exchange', 'ok');
          finishAuth(data.user);
        }
      } catch (e) {
        console.error('Yandex auth exchange error', e);
        sendAppEvent('oauth_exchange', 'error');
        // Обмен не дошел до сервера — объясняем, что нет связи, а не
        // предлагаем войти заново: когда связь вернется, экран сам повторит
        // проверку сессии (кука могла и встать, если сервер успел ответить)
        if (checkIfNetworkFailure(e)) {
          setIsStartupNetworkError(true);
        }
      } finally {
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsAuthLoading(false);
      }
    };

    // Ссылка подтверждения почты из письма: /auth/confirm/<токен>. Токен
    // отправляет СТРАНИЦА, а не сам почтовый клиент — ручка подтверждения
    // отвечает на POST, чтобы предзагрузчики писем и антивирусы не «сжигали»
    // одноразовую ссылку до того, как человек по ней перейдет
    const confirmEmail = async (confirmToken) => {
      try {
        const res = await fetchWithTimeout(`${API_BASE}/auth/email/confirm`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          credentials: 'include',
          body: JSON.stringify({token: confirmToken}),
        }, AUTH_CHECK_TIMEOUT_MS);
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          sendAppEvent('email_confirm', 'error');
          // Подтвердить не вышло (ссылка протухла или уже использована) —
          // показываем обычный экран входа, там же есть вход по паролю
          dispatch(showToast(data.error || 'Ссылка устарела', 'error', 6000));
          checkMe();
          return;
        }

        sendAppEvent('email_confirm', 'ok');
        if (data.needsOnboarding) {
          setNeedsOnboarding(true);
        } else if (data.user) {
          finishAuth(data.user);
        }
      } catch (e) {
        console.error('Email confirm error', e);
        sendAppEvent('email_confirm', 'error');
        // Ждем, а не запускаем вдогонку: иначе finally ниже снял бы сплеш, и
        // до ответа проверки мелькала бы страница входа
        await checkMe();
      } finally {
        window.history.replaceState({}, document.title, '/');
        setIsAuthLoading(false);
      }
    };

    const confirmToken = window.location.pathname.startsWith(EMAIL_CONFIRM_URL_PREFIX)
      ? decodeURIComponent(
        window.location.pathname.slice(EMAIL_CONFIRM_URL_PREFIX.length).split('/')[0] || ''
      ).trim()
      : '';

    if (passwordResetToken) {
      // Смена пароля идет своим экраном: заходить по старой сессии не нужно
      setIsAuthLoading(false);
    } else if (confirmToken) {
      confirmEmail(confirmToken);
    } else if (code) {
      exchangeCode();
    } else {
      checkMe();
    }
  }, [finishAuth, checkMe, dispatch, passwordResetToken]);

  // Интерфейс дошел до первого кадра. Вместе с app:boot из index.js дает
  // ответ на главный вопрос статистики: сколько людей вообще увидели
  // приложение, а не просто выполнили бандл. Значение — корзина времени от
  // открытия страницы: на мобильном интернете это и есть основной отвал
  useEffect(() => {
    sendAppEvent('render', getDurationBucket(performance.now()));
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (window.__skipBeforeUnload) return;
      event.preventDefault();
      event.returnValue = 'Все несохраненные изменения будут потеряны. Вы действительно хотите покинуть страницу?';
      return event.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    setConnectionLostHandler(() => setConnectionLost(true));
    return () => removeConnectionLostHandler();
  }, []);

  useEffect(() => {
    setSessionSupersededHandler(() => setSessionSuperseded(true));
    return () => removeSessionSupersededHandler();
  }, []);

  useEffect(() => {
    setReconnectStatusHandler(setReconnectStatus);
    return () => removeReconnectStatusHandler();
  }, []);

  useEffect(() => {
    const handleVersionMismatch = (event) => {
      setGameInteractionDisabled(true);
      setVersionMismatchServerVersion(event?.detail?.serverVersion ?? null);
      setVersionMismatch(true)
    };
    window.addEventListener('versionmismatch', handleVersionMismatch);
    return () => window.removeEventListener('versionmismatch', handleVersionMismatch);
  }, []);

  useEffect(() => {
    const handleGameEndedByHost = () => {
      const state = store.getState();
      const gameResults = selectGameResults(state);
      if (!gameResults) {
        setGameEndedByHost(true)
      }
    };
    window.addEventListener('gameendedbyhost', handleGameEndedByHost);
    return () => window.removeEventListener('gameendedbyhost', handleGameEndedByHost);
  }, []);

  useEffect(() => {
    if (connectionLost && screen === SCREENS.GAME) {
      sessionStorage.setItem('reconnectToLobbyList', '1');
      window.__skipBeforeUnload = true;
      window.location.reload();
    }
  }, [connectionLost, screen]);


  // Редактор миссий (админ): тот же путь, что у локальной партии
  // (выбор поля -> состав игроков -> расстановка -> матч), но каждое действие
  // пишется в ленту миссии
  const [isMissionEditorMode, setIsMissionEditorMode] = useState(false);

  const handleFieldSelect = (field) => {
    setSelectedField(field);
    setScreen(SCREENS.HOT_SEAT_INIT);
  };

  const handleHotSeatPlayersSubmit = (
    submittedPlayers, roundCount, isSpectatorModeEnabled = false,
    isTimerEnabledOption = false, isPlannerBotsEnabled = false, chosenGroundBackground = null
  ) => {
    // Партия из одних ботов стартует с поднятой галочкой «Не отображать
    // всплывающие окна»: человек ее только смотрит. Режим зрителя тут не
    // включается — собранная тумблерами партия «все боты» играется с обычным
    // UI (см. HotSeatPlayersInitPage), меняется одна эта настройка.
    // Редактор миссий исключен явно: там автор играет за ВСЕХ, признак бота у
    // них снимается НИЖЕ, и по составу партия выглядела бы ботовой
    const isAllBotsGame = !isMissionEditorMode
      && checkIfAllPlayersAreBots(submittedPlayers);
    // Сброс стора первым делом — иначе поле монтировалось на игроках демо-матча
    // из меню и не загружало купленные скины отрядов (см. prepareStoreForLocalGame)
    prepareStoreForLocalGame(store.dispatch, {
      isPlannerBotsEnabled,
      isSpectatorModeEnabled,
      isAllBotsGame,
    });
    setIsSpectatorGame(Boolean(isSpectatorModeEnabled));
    setIsLocalTimerEnabled(Boolean(isTimerEnabledOption));
    // Выставляется при КАЖДОМ старте локальной партии: первый бой новичка
    // идет мимо формы и подложку не называет — ему случайная, а не оставшаяся
    // от прошлой партии. С перечнем подложек выбор сверяет сама партия (см.
    // GameContent)
    setLocalGroundBackground(chosenGroundBackground ?? null);
    setGameRoundCount(roundCount);
    // В редакторе автор играет за ВСЕХ, включая помеченных ботами: движок
    // ботов не должен перехватывать их ходы, пока автор не нажмёт «Передать
    // ИИ». Исходные флаги сохраняются в черновике миссии — при прохождении
    // эти игроки снова станут ботами
    setPlayers(isMissionEditorMode
      ? submittedPlayers.map((player) => ({...player, isBot: false}))
      // Боты локальной партии помечаются значком с иконкой по своему бонусу,
      // а живым местам ставится признак форы новичкам, если она положена
      // игроку за устройством (см. prepareLocalMatchPlayers). Редактор миссий
      // сюда не попадает: там все игроки под автором, исходные флаги живут в
      // черновике миссии, а штраф форы запекся бы в ленту миссии
      : prepareLocalMatchPlayers(submittedPlayers, auth));

    if (isMissionEditorMode) {
      // Живой игрок миссии — первый не-бот в составе: его ходы в ленту не
      // пишутся, при прохождении их сыграет реальный игрок
      // Цвет игрока фиксируем ПОЗЖЕ, по составу партии: цвета переназначает
      // applyFactionColorsToLocalGame по приоритетам фракций, и выбранный
      // здесь цвет к старту матча уже другой. Надёжен только индекс
      store.dispatch(missionEditorStart({
        id: `mission_${Date.now()}`,
        title: '',
        description: '',
        finalRound: roundCount ?? null,
        playerIndex: Math.max(0, submittedPlayers.findIndex((player) => !player.isBot)),
        botFlags: submittedPlayers.map((player) => Boolean(player.isBot)),
        players: submittedPlayers,
      }));
    }

    setScreen(SCREENS.GAME);
  };

  // Просмотр записи матча: всегда режим зрителя, всплывающие окна по ходу
  // партии скрыты так же, как в спектаторской партии — открыт остается
  // только финальный экран конца игры
  const handleWatchReplay = async (matchId) => {
    try {
      const res = await fetch(`${API_BASE}/matches/${matchId}/actions`, {credentials: 'include'});
      setIsReplayAccessDenied(false);
      if (!res.ok) {
        // Кнопку показал клиент, а решает сервер — по куке. Отказ бывает, если
        // в соседней вкладке вошли под другим аккаунтом или роль сняли при
        // открытой сессии: это не «записи нет»
        setIsReplayAccessDenied(res.status === 401 || res.status === 403);
        setIsReplayUnavailableModalVisible(true);
        return;
      }
      const data = await res.json();
      // Запись прохождения обучения и запись локальной партии (обе — только
      // админам, проверяет сервер): это локальные партии, и проигрываются они
      // по локальным правилам
      const isTutorialRecording = Boolean(data.isTutorial);
      const isLocalRunRecording = Boolean(data.isLocal);
      const isRecordedRun = isTutorialRecording || isLocalRunRecording;
      const actions = isRecordedRun
        ? prepareTutorialReplayActions(data.actions || [])
        : (data.actions || []);
      // Партия, брошенная до загрузки поля, — смотреть нечего
      if (isRecordedRun && !actions.some((action) => action?.type === 'LOAD_STATE')) {
        setIsReplayUnavailableModalVisible(true);
        return;
      }

      // ЛЕНИВЫЙ запасной путь: у записей, сделанных до того, как награды стали
      // попадать в саму запись, экшена с наградами нет — только тогда лезем за
      // карточкой матча. У новых записей запроса не будет вовсе.
      // Любая ошибка здесь НЕ ломает просмотр: реплей играется как прежде,
      // итоги просто покажутся без наград — ровно как было до этой правки.
      // У обучения итогов партии нет вовсе
      let fallbackResults = null;
      if (!isRecordedRun && !actions.some((action) => action?.type === 'SET_EXTENDED_GAME_RESULTS')) {
        try {
          const matchRes = await fetch(`${API_BASE}/matches/${matchId}`, {credentials: 'include'});
          if (matchRes.ok) {
            const matchData = await matchRes.json();
            fallbackResults = {
              extendedResults: buildExtendedResultsFromMatch(matchData?.match),
              isRanked: Boolean(matchData?.match?.isRanked),
            };
          }
        } catch (rewardsError) {
          console.error('Replay rewards fallback error', rewardsError);
        }
      }

      store.dispatch(setGameType(
        GAME_TYPES.REPLAY,
        isRecordedRun ? {rulesGameType: GAME_TYPES.LOCAL} : undefined
      ));
      store.dispatch(setHideModalsExceptGameEnd(true));
      setReplayActions(actions);
      setReplayFallbackResults(fallbackResults);
      setIsTutorialReplay(isTutorialRecording);
      setIsSpectatorGame(true);
      setScreen(SCREENS.GAME);
    } catch (error) {
      console.error('Watch replay error:', error);
      setIsReplayAccessDenied(false);
      setIsReplayUnavailableModalVisible(true);
    }
  };

  // Кнопка "Рейтинговый матч" (меню, промо-окно, задания) открывает экран
  // настройки поиска, а не сам поиск. «Назад» с него — туда, откуда пришли:
  // в меню или в список лобби (там своя кнопка «Рейтинговый матч»)
  const [rankMatchSetupReturnScreen, setRankMatchSetupReturnScreen] = useState(SCREENS.MENU);
  const openRankMatchSetup = useCallback(() => {
    setRankMatchSetupReturnScreen(SCREENS.MENU);
    setScreen(SCREENS.RANK_MATCH_SETUP);
  }, []);
  const openRankMatchSetupFromLobbyList = useCallback(() => {
    setRankMatchSetupReturnScreen(SCREENS.LOBBY_LIST);
    setScreen(SCREENS.RANK_MATCH_SETUP);
  }, []);
  // Отмена поиска — обратно к настройке; куда с нее вести «Назад», уже
  // запомнено при входе
  const returnToRankMatchSetup = useCallback(() => {
    setScreen(SCREENS.RANK_MATCH_SETUP);
  }, []);

  // «Начать поиск» на экране настройки ставит игрока в пул автоматического
  // матч-мейкинга и переводит на экран ожидания — сама лобби не создает,
  // это делает сервер (см. RankMatchWaiting/rank-match/join).
  // opponentCounts — выбор ограничения длительности с экрана настройки (null —
  // не загрузился, сервер возьмет сохраненное в профиле)
  const handleRankedGame = useCallback(async (selectedOpponentCounts = null) => {
    if (isRankMatchJoinInFlightRef.current) {
      return;
    }
    // Не массив (вызов без выбора, событие клика) — выбор неизвестен
    const opponentCounts = Array.isArray(selectedOpponentCounts) ? selectedOpponentCounts : null;
    isRankMatchJoinInFlightRef.current = true;
    try {
      const outcome = await requestRankMatchJoin({playerName: auth.name, playerId: auth.id, opponentCounts});
      switch (outcome.kind) {
        case RANK_MATCH_JOIN_OUTCOMES.WAITING:
          // Ограничение по числу противников, с которым игрок встал в поиск,
          // — экран ожидания только показывает его (rankMatch.js)
          setRankMatchOpponentCounts(outcome.opponentCounts || opponentCounts);
          setRankMatchSearchId(outcome.searchId);
          // Игрок встал в поиск рейтингового матча — самая вовлеченная аудитория
          trackAdGoal(AD_GOALS.RANKED_MATCH_STARTED, {userId: auth.id});
          setScreen(SCREENS.RANK_MATCH_WAITING);
          break;
        case RANK_MATCH_JOIN_OUTCOMES.LOBBY:
          // Игрок уже состоит участником-игроком в другом (в т.ч. рейтинговом)
          // лобби — переходим сразу в него вместо запуска нового поиска
          setEnteredLobbyId(outcome.lobbyId);
          setScreen(SCREENS.LOBBY);
          break;
        case RANK_MATCH_JOIN_OUTCOMES.NOT_ENOUGH_FACTIONS:
          setNotEnoughFactionsModal({availableCount: outcome.availableCount});
          break;
        default:
          // Молча проглоченный отказ выглядел для игрока как неработающая
          // кнопка — теперь он видит, что поиск не начался
          console.error('Ranked game error', outcome.reason);
          sendNetworkEvent('rank_match_join_failed', outcome.reason);
          setRankMatchSearchNotice(RANK_MATCH_SEARCH_NOTICES.JOIN_FAILED);
      }
    } finally {
      isRankMatchJoinInFlightRef.current = false;
    }
  }, [auth.name, auth.id]);

  // Этот же игрок начал поиск в другой вкладке или на другом устройстве —
  // здесь поиск больше не идет
  const handleRankMatchSearchSuperseded = useCallback(() => {
    setScreen(SCREENS.MENU);
    setRankMatchSearchNotice(RANK_MATCH_SEARCH_NOTICES.SEARCH_SUPERSEDED);
  }, []);

  const handleRankMatchLobbyReady = useCallback((lobbyId) => {
    setEnteredLobbyId(lobbyId);
    setScreen(SCREENS.LOBBY);
  }, []);

  // Старая логика кнопки "Рейтинговый матч" (присоединение к открытому
  // вручную созданному рейтинговому лобби или создание нового с собой в роли
  // создателя), перенесенная в Админку — доступна только администраторам
  const handleAdminCreateRankedLobby = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/lobbies/ranked`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({playerName: auth.name, playerId: auth.id}),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.error === 'NOT_ENOUGH_FACTIONS') {
          setNotEnoughFactionsModal({availableCount: data.availableCount || 0});
          return;
        }
        throw new Error(data.message || 'Failed to create ranked lobby');
      }
      const data = await res.json();
      setEnteredLobbyId(data.lobbyId);
      setScreen(SCREENS.LOBBY);
    } catch (e) {
      console.error('Admin create ranked lobby error', e);
      alert(e.message || 'Не удалось создать рейтинговое лобби');
    }
  }, [auth.name, auth.id]);

  const checkIfAlreadyInLobby = useCallback(async (playerId) => {
    try {
      const res = await fetch(`${API_BASE}/players/${playerId}/active-lobby`);
      if (!res.ok) throw new Error('Check failed');
      const data = await res.json();
      if (data && data.lobbyId) {
        setEnteredLobbyId(data.lobbyId);
        setScreen(SCREENS.LOBBY);
      }
    } catch (e) {
      console.error('Active lobby check error', e);
      setScreen(SCREENS.LOBBY_LIST);
    }
  }, []);

  // Вход по ссылке-приглашению в лобби: id лобби положен в sessionStorage при
  // открытии ссылки (см. эффект разбора реферальных ссылок выше). Здесь —
  // попытка сразу присоединиться. Ошибки те же, что у входа из списка лобби:
  // мало фракций, игрок уже в другом лобби (сервер возвращает его id), лобби
  // уже нет
  const joinInvitedLobby = useCallback(async (lobbyId) => {
    // Приглашение считается израсходованным, только когда сервер дал
    // определенный ответ (вошли/отказ). Сетевой сбой его НЕ сжигает: ключ
    // остается, и попытка повторится при следующем запуске приложения
    const consumeInvite = () => sessionStorage.removeItem(LOBBY_INVITE_STORAGE_KEY);

    try {
      // encodeURIComponent — вторая линия после проверки формата в
      // parseInviteLobbyIdFromPath: id приходит из открытой пользователем
      // ссылки, и он не должен уметь менять путь запроса
      const res = await fetch(`${API_BASE}/lobbies/${encodeURIComponent(lobbyId)}/join`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        // С куками: в лобби «Битвы с AI» сервер пускает только по настоящему
        // входу (JWT), обычным лобби кука не мешает
        credentials: 'include',
        body: JSON.stringify({playerName: auth.name, playerId: auth.id}),
      });

      // Сервер ответил определенно — приглашение израсходовано в любом исходе
      const data = await res.json().catch(() => ({}));
      consumeInvite();

      const outcome = resolveInvitedLobbyJoinOutcome(res, data);
      switch (outcome.kind) {
        case INVITED_LOBBY_JOIN_OUTCOMES.ENTER_LOBBY:
        case INVITED_LOBBY_JOIN_OUTCOMES.ENTER_OTHER_LOBBY:
          setEnteredLobbyId(outcome.lobbyId || lobbyId);
          setScreen(SCREENS.LOBBY);
          return;
        case INVITED_LOBBY_JOIN_OUTCOMES.NOT_ENOUGH_FACTIONS:
          setNotEnoughFactionsModal({availableCount: outcome.availableCount});
          return;
        case INVITED_LOBBY_JOIN_OUTCOMES.LOBBY_IS_FULL:
          setLobbyIsFullModal({maxPlayers: outcome.maxPlayers});
          setScreen(SCREENS.LOBBY_LIST);
          return;
        case INVITED_LOBBY_JOIN_OUTCOMES.RANK_MATCH_NOT_ALLOWED:
          setLobbyListInitialError(RANK_MATCH_JOIN_NOT_ALLOWED_MESSAGE);
          setScreen(SCREENS.LOBBY_LIST);
          return;
        case INVITED_LOBBY_JOIN_OUTCOMES.RANK_MATCH_PHASE_STARTED:
          setLobbyListInitialError(RANK_MATCH_PHASE_STARTED_MESSAGE);
          setScreen(SCREENS.LOBBY_LIST);
          return;
        default:
          console.warn('Invited lobby unavailable', outcome.status, outcome.error);
          setLobbyListInitialError(INVITED_LOBBY_UNAVAILABLE_MESSAGE);
          setScreen(SCREENS.LOBBY_LIST);
      }
    } catch (e) {
      // Сюда доходит только сетевой сбой (fetch отверг промис): ответы
      // сервера, включая 404 и 5xx, разобраны выше. Приглашение при этом НЕ
      // расходуется — попытка повторится при следующем запуске приложения
      console.error('Invited lobby join network error', e);
      setLobbyListInitialError(INVITED_LOBBY_NETWORK_ERROR_MESSAGE);
      setScreen(SCREENS.LOBBY_LIST);
    }
  }, [auth.name, auth.id]);

  // «Битва с AI Claude Opus»: вход в лобби дня из главного меню (кнопка или
  // промо-модалка; гостя меню сюда не пускает — у него окно входа). Ответ
  // разбирается тем же resolveInvitedLobbyJoinOutcome, что и вход по
  // приглашению. Полное лобби или начавшийся матч сервер не отвергает, а
  // пускает зрителем (joinedAsSpectator) — это тоже вход в лобби, объяснит
  // его уже экран лобби. Куки обязательны: в ИИ-лобби пускают только по
  // настоящему входу (JWT), без них сервер ответит 401. Отказ — модалкой поверх
  // меню, а не тостом: тосты рисуются только на экране матча
  const joinAiPromoLobby = useCallback(async (lobbyId) => {
    if (!lobbyId || isAiPromoJoinInFlightRef.current) {
      return;
    }
    isAiPromoJoinInFlightRef.current = true;
    try {
      const res = await fetch(`${API_BASE}/lobbies/${encodeURIComponent(lobbyId)}/join`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({playerName: auth.name, playerId: auth.id}),
      });
      const data = await res.json().catch(() => ({}));

      const outcome = resolveInvitedLobbyJoinOutcome(res, data);
      switch (outcome.kind) {
        case INVITED_LOBBY_JOIN_OUTCOMES.ENTER_LOBBY:
        case INVITED_LOBBY_JOIN_OUTCOMES.ENTER_OTHER_LOBBY:
          setEnteredLobbyId(outcome.lobbyId || lobbyId);
          setScreen(SCREENS.LOBBY);
          return;
        case INVITED_LOBBY_JOIN_OUTCOMES.NOT_ENOUGH_FACTIONS:
          // В ИИ-лобби сервер сам докидывает фракции до четырех — сюда дойдет
          // разве что старая сборка сервера
          setNotEnoughFactionsModal({availableCount: outcome.availableCount});
          return;
        default:
          console.warn('AI promo lobby join refused', outcome.status, outcome.error);
          setAiLobbyJoinErrorText(getAiLobbyJoinErrorText(outcome));
      }
    } catch (e) {
      console.error('AI promo lobby join network error', e);
      setAiLobbyJoinErrorText(getAiLobbyJoinErrorText({kind: AI_LOBBY_JOIN_NETWORK_ERROR}));
    } finally {
      isAiPromoJoinInFlightRef.current = false;
    }
  }, [auth.name, auth.id]);

  // Проверка «уже в лобби» должна дергаться РОВНО раз на пользователя, как до
  // появления приглашений: у эффекта ниже теперь есть зависимости, меняющиеся
  // и после входа (принятие соглашений, обновление профиля), а повторный
  // вызов уводил бы игрока с текущего экрана в найденное лобби
  const alreadyInLobbyCheckedForRef = useRef(null);
  // Попытка входа по приглашению уже запущена в этом запуске приложения
  const inviteJoinStartedRef = useRef(false);
  // Экран согласия еще не пройден (документы и подтверждение 18 лет) — одним
  // признаком и для эффекта ниже, и для подмены экрана MENU
  const isLegalConsentRequired = checkIfLegalConsentRequired(auth);

  useEffect(() => {
    if (!auth.id) return;
    // До согласий (документы и подтверждение 18 лет) экран MENU подменяется
    // LegalConsentGate — переход в лобби, по приглашению или в уже начатое,
    // обошел бы его. Ни приглашение, ни проверку лобби не теряем: эффект
    // перезапустится, когда согласия проставятся (см. LegalConsentGate)
    if (isLegalConsentRequired) return;

    const pendingInviteLobbyId = sessionStorage.getItem(LOBBY_INVITE_STORAGE_KEY);
    if (pendingInviteLobbyId) {
      // Одноразово в рамках запуска: эффект перезапускается на изменения
      // профиля, а тащить игрока в лобби повторно нельзя. Сам ключ снимает
      // joinInvitedLobby — только получив определенный ответ сервера
      if (inviteJoinStartedRef.current) return;
      inviteJoinStartedRef.current = true;
      joinInvitedLobby(pendingInviteLobbyId);
      return;
    }

    if (alreadyInLobbyCheckedForRef.current === auth.id) return;
    alreadyInLobbyCheckedForRef.current = auth.id;
    checkIfAlreadyInLobby(auth.id);
  }, [
    auth.id,
    isLegalConsentRequired,
    checkIfAlreadyInLobby,
    joinInvitedLobby,
  ]);

  const goToMainMenu = useCallback(() => {
    // Выход в меню закрывает и режим редактора миссий: следующий локальный
    // матч не должен внезапно продолжить запись
    setIsMissionEditorMode(false);
    setCampaignMissionId(null);
    setScreen(SCREENS.MENU)
  }, []);

  // === Сюжетные кампании ===
  // Идентификатор проходимой миссии: как и с обучением, входит в key
  // GameContent, поэтому перезапуск миссии полностью перемонтирует партию
  // Чью историю начислений кристаллов смотрим: свою из профиля или чужую,
  // открытую админом (как контексты истории матчей)
  const [ratingCrystalsContext, setRatingCrystalsContext] = useState(null);
  const [userInventoryContext, setUserInventoryContext] = useState(null);
  const [campaignFactionId, setCampaignFactionId] = useState(null);
  const [campaignMissionId, setCampaignMissionId] = useState(null);

  const startCampaignMission = useCallback((missionId) => {
    store.dispatch(resetState());
    setCampaignMissionId(missionId);
    setScreen(SCREENS.GAME);
  }, []);

  const exitMissionToScreen = useCallback((targetScreen) => {
    store.dispatch(resetState());
    setCampaignMissionId(null);
    setScreen(targetScreen);
  }, []);

  // === Обучение ===
  // Идентификатор главы, с которой открыт GAME-экран в режиме обучения.
  // Смена ключа (см. key у GameContent) полностью перемонтирует партию —
  // тот же механизм и для «следующей главы», и для повторного прохождения
  const [tutorialChapterId, setTutorialChapterId] = useState(null);

  useEffect(() => {
    if (screen !== SCREENS.GAME && tutorialChapterId) {
      setTutorialChapterId(null);
    }
  }, [screen, tutorialChapterId]);

  // Уход с экрана игры гасит всё, что относится к КОНКРЕТНОЙ партии. Иначе
  // состояние просмотра реплея переживало бы его: реплей выставляет режим
  // зрителя и скрытие всплывающих окон, а обучение, сетевой матч и миссии
  // ничего этого не выставляют — они просто наследовали то, что осталось.
  // Симптом: после просмотра реплея в обучении пропадали галочка завершения
  // фазы и песочные часы, а футер вел себя как у зрителя.
  // Чистим именно здесь, а не в каждой точке старта: так новый режим не сможет
  // забыть про сброс
  const prevScreenRef = useRef(screen);
  useEffect(() => {
    const prevScreen = prevScreenRef.current;
    prevScreenRef.current = screen;
    // Условие — именно УХОД с экрана игры, а не «мы сейчас не в игре». Разница
    // принципиальна: режим редактора миссий выставляется одним батчем со
    // сменой экрана (onCreateMission ниже), и проверка «не GAME» погасила бы
    // флаг тем же коммитом — редактор молча перестал бы записывать ленту.
    // Свой ref, а не общий screenRef выше: тот обновляется эффектом, который
    // объявлен раньше, и к этому моменту уже хранит новый экран
    if (!checkIfMatchSessionShouldReset(prevScreen, screen)) {
      return;
    }
    setReplayActions(null);
    setReplayFallbackResults(null);
    setIsTutorialReplay(false);
    setIsSpectatorGame(false);
    store.dispatch(setHideModalsExceptGameEnd(false));
    // Тот же класс протечки: выход через InGameMenu зовет setScreen напрямую,
    // минуя goToMainMenu, поэтому идентификатор миссии оставался выставленным.
    // А в бутстрапе GameContent ветка миссии проверяется РАНЬШЕ ветки обучения
    // — «миссия -> главное меню -> обучение» грузила миссию вместо главы
    setCampaignMissionId(null);
    setIsMissionEditorMode(false);
  }, [screen]);

  const startTutorialChapter = useCallback((chapterId) => {
    trackAdGoal(AD_GOALS.TUTORIAL_STARTED, {params: {chapterId}});
    store.dispatch(resetState());
    setTutorialChapterId(chapterId);
    setScreen(SCREENS.GAME);
  }, []);

  const exitTutorialToScreen = useCallback((targetScreen) => {
    store.dispatch(resetState());
    setTutorialChapterId(null);
    setScreen(targetScreen);
  }, []);

  // Первый бой новичка — готовая локальная партия против ботов (состав и
  // поле см. в configs/newbieBattle). Сюда ведут кнопка «В битву» с
  // поздравления за последнюю главу обучения и приглашение в главном меню:
  // экраны выбора поля и состава пропускаются, настраивать новичку нечего.
  // Ответ — ушел ли игрок в бой: при отказе поздравление остается на экране
  // и предлагает попробовать еще раз
  // «Найти соперников» из приглашения в обычный матч: собираем игроку свое
  // лобби и ведем прямо в него — дальше сервер подсаживает туда соперников
  // (см. utils/lobby/createOwnLobby). Не получилось — остаемся в меню и
  // открываем список лобби, там игрок соберет матч сам
  const findOpponents = async () => {
    const lobbyId = await createOwnLobby(auth);
    if (!lobbyId) {
      setScreen(SCREENS.LOBBY_LIST);
      return;
    }
    setEnteredLobbyId(lobbyId);
    setScreen(SCREENS.LOBBY);
  };

  const isNewbieBattleInFlightRef = useRef(false);
  const startNewbieBattle = async () => {
    if (isNewbieBattleInFlightRef.current) {
      return false;
    }
    isNewbieBattleInFlightRef.current = true;
    try {
      const field = GAME_FIELDS.find((item) => item.id === NEWBIE_BATTLE_FIELD_ID);
      if (!field) {
        console.error('[NewbieBattle] field not found', NEWBIE_BATTLE_FIELD_ID);
        return false;
      }
      // Цвета игроков живут в самом поле — состав собирается по ним
      const response = await fetch(field.path);
      const data = await response.json();
      const battlePlayers = buildNewbieBattlePlayers(data.playerColors, {playerName: auth?.name});
      if (battlePlayers.length === 0) {
        console.error('[NewbieBattle] failed to build players', field.id);
        return false;
      }

      // Выход из обучения: по tutorialChapterId GameContent выбирает ветку
      // бутстрапа и собирает свой key — без сброса партия стартовала бы как
      // глава обучения
      setTutorialChapterId(null);
      setCampaignMissionId(null);
      setIsMissionEditorMode(false);
      setSelectedField(field);
      handleHotSeatPlayersSubmit(battlePlayers, NEWBIE_BATTLE_ROUND_COUNT);
      return true;
    } catch (error) {
      console.error('[NewbieBattle] failed to start battle', error);
      return false;
    } finally {
      isNewbieBattleInFlightRef.current = false;
    }
  };

  // Переход в магазин на вкладку кристаллов — из UserHeader (клик по нему,
  // модалка обмена кристаллов на нумены) на любом экране, где он отображается
  const goToShopCrystals = useCallback(() => {
    setShopInitialTab(SHOP_TABS.CRYSTALS);
    setScreen(SCREENS.SHOP);
  }, []);

  // Переход в магазин на вкладку премиума — ссылка «Купить» в своем профиле,
  // когда премиум сейчас не действует
  const goToShopPremium = useCallback(() => {
    setShopInitialTab(SHOP_TABS.PREMIUM);
    setScreen(SCREENS.SHOP);
  }, []);

  const handleLobbyLeave = useCallback(() => {
    setScreen(SCREENS.LOBBY_LIST)
  }, []);

  const handleGameStart = useCallback(() => {
    setScreen(SCREENS.GAME)
  }, []);

  // Колода скрыта, если ее уже закрывали на этом устройстве или отметка стоит
  // в профиле. Юридический экран приоритетнее: пока согласия нет, меню (а с
  // ним и колода) не показывается вовсе
  const isIntroSlidesVisible = isIntroSlidesPending && !auth.hasSeenIntroSlides;

  const handleIntroSlidesClose = useCallback(() => {
    setIsIntroSlidesPending(false);
    markIntroSlidesShown();
    // У гостя профиля еще нет — метку сохраняем только вошедшему
    if (auth.id) {
      markIntroSlidesSeen(dispatch, auth);
    }
  }, [dispatch, auth]);

  return (
    <>
      <BackgroundMusic isInGame={screen === SCREENS.GAME}/>
      {!gameInteractionDisabled && (screen !== SCREENS.GAME ? (
          <div className={`menu-screens-container ${screen} ${addClassNameIfTrue(isOpaque, 'opaque')}`}>
            <CampfireSparks/>
            {screen === SCREENS.AUTH && (
              isAuthLoading ? (
                <AppSplash/>
              // AUDIT_LOGIN_TEMP: страница входа для аудита — только по своему
              // адресу и только пока нет сессии (иначе экран AUTH не показан)
              ) : isAuditLoginRoute ? (
                <AuditLoginPage onAuth={finishAuth}/>
              ) : isStartupNetworkError ? (
                <NetworkErrorPage onServerAvailable={retryAuthCheck}/>
              ) : devLogin ? (
                <UserNamePage devMode onAuth={finishAuth}/>
              ) : needsOnboarding ? (
                <UserNamePage onboarding onAuth={finishAuth}/>
              ) : (
                passwordResetToken ? (
                  <PasswordResetPage
                    token={passwordResetToken}
                    onAuthSuccess={(data) => {
                      setPasswordResetToken('');
                      window.history.replaceState({}, document.title, '/');
                      handleEmailAuthSuccess(data);
                    }}
                    onCancel={() => {
                      setPasswordResetToken('');
                      window.history.replaceState({}, document.title, '/');
                    }}
                  />
                ) : (
                <YandexAuthPage onDevLogin={handleDevLogin} onEmailAuthSuccess={handleEmailAuthSuccess}/>
                )
              )
            )}
            {screen === SCREENS.MENU && (
              isLegalConsentRequired ? (
                <LegalConsentGate/>
              ) : (
                <MainMenu
                  onDevLogin={handleDevLogin}
                  onEmailAuthSuccess={handleEmailAuthSuccess}
                  onTutorial={() => setScreen(SCREENS.TUTORIAL_MISSIONS)}
                  onNetworkGame={() => setScreen(SCREENS.LOBBY_LIST)}
                  onRankedGame={openRankMatchSetup}
                  onHotSeatGame={() => setScreen(SCREENS.FIELD_SELECT)}
                  // Приглашения новичку: готовый бой с ботами и сбор
                  // соперников в свое лобби (см. utils/menuPromo)
                  onBotMatch={startNewbieBattle}
                  onFindOpponents={findOpponents}
                  onAiMatch={joinAiPromoLobby}
                  onInventory={() => setScreen(SCREENS.INVENTORY)}
                  onInventoryRankSkins={() => {
                    setInventoryFocusSection(INVENTORY_FOCUS_SECTIONS.RANK_SKINS);
                    setScreen(SCREENS.INVENTORY);
                  }}
                  onProfile={() => setScreen(SCREENS.PROFILE)}
                  onQuests={() => setScreen(SCREENS.QUESTS)}
                  onGloryHall={() => setScreen(SCREENS.GLORY_HALL)}
                  onBestCollections={() => setScreen(SCREENS.BEST_COLLECTIONS)}
                  onSettings={() => setScreen(SCREENS.SETTINGS)}
                  onRules={() => dispatch(showConstructionPhaseInfoModal())}
                  onAcknowledgments={() => setScreen(SCREENS.ACKNOWLEDGMENTS)}
                  onLicenseAgreement={() => setScreen(SCREENS.LICENSE_AGREEMENT)}
                  onPrivacyPolicy={() => setScreen(SCREENS.PRIVACY_POLICY)}
                  onContacts={() => setScreen(SCREENS.CONTACTS)}
                  onAdmin={() => setScreen(SCREENS.ADMIN)}
                  onPartner={() => {
                    // Кнопка «Партнерка»: своя партнерская страница, только просмотр
                    setPartnerFormContext({
                      userId: auth.id,
                      userName: auth.name,
                      isSelfView: true,
                      returnScreen: SCREENS.MENU,
                    });
                    setScreen(SCREENS.PARTNER_FORM);
                  }}
                  onShop={() => {
                    setShopInitialTab(null);
                    setScreen(SCREENS.SHOP);
                  }}
                  onGoToCrystals={goToShopCrystals}
                  onOpenUserProfile={(userId, userName) => {
                    // Клик по нику партнера в модалке реферальной награды —
                    // профиль партнера (стата и история матчей)
                    setGlorySelectedUser({id: userId, name: userName});
                    setUserProfileReturnScreen(SCREENS.MENU);
                    setScreen(SCREENS.USER_PROFILE);
                  }}
                  isIntroSlidesVisible={isIntroSlidesVisible}
                />
              )
            )}
            {/* Колода поверх меню, а не вместо него: за ней уже грузится
                витрина с демо-матчем, и закрывший колоду попадает на живое
                поле, а не на пустой экран */}
            {screen === SCREENS.MENU && isIntroSlidesVisible && (
              <IntroSlides onClose={handleIntroSlidesClose}/>
            )}
            {screen === SCREENS.FIELD_SELECT && (
              <FieldSelectMenu
                onSelectField={handleFieldSelect}
                onBack={goToMainMenu}
              />
            )}
            {screen === SCREENS.HOT_SEAT_INIT && (
              <HotSeatPlayersInitPage
                field={selectedField}
                // В редакторе миссий та же страница собирает состав будущей
                // миссии: там игроки играются автором, и значков ботов нет
                isMissionEditorMode={isMissionEditorMode}
                onSubmit={handleHotSeatPlayersSubmit}
                onBack={() => setScreen(SCREENS.FIELD_SELECT)}
                onGoToCrystals={goToShopCrystals}
                onOpenInventory={() => setScreen(SCREENS.INVENTORY)}
              />
            )}
            {screen === SCREENS.LOBBY_LIST && (
              <LobbyListPage
                initialError={lobbyListInitialError}
                onInitialErrorShown={() => setLobbyListInitialError(null)}
                onEnterLobby={(lobbyId) => {
                  setEnteredLobbyId(lobbyId);
                  setScreen(SCREENS.LOBBY);
                }}
                onNotEnoughFactions={(availableCount) => setNotEnoughFactionsModal({availableCount})}
                onLobbyIsFull={(maxPlayers) => setLobbyIsFullModal({maxPlayers})}
                onBack={goToMainMenu}
                onShop={() => setScreen(SCREENS.SHOP)}
                onDevLogin={handleDevLogin}
                onEmailAuthSuccess={handleEmailAuthSuccess}
                // Из списка лобби — к настройке поиска рейтингового матча
                onRankedMatch={openRankMatchSetupFromLobbyList}
              />
            )}
            {screen === SCREENS.RANK_MATCH_SETUP && (
              <RankMatchSetup
                onStartSearch={handleRankedGame}
                onBack={rankMatchSetupReturnScreen === SCREENS.LOBBY_LIST
                  ? () => setScreen(SCREENS.LOBBY_LIST)
                  : goToMainMenu}
              />
            )}
            {screen === SCREENS.RANK_MATCH_WAITING && (
              <RankMatchWaiting
                playerId={auth.id}
                searchId={rankMatchSearchId}
                onLobbyReady={handleRankMatchLobbyReady}
                // Отмена поиска — обратно к настройке поиска, а не в меню
                onCancel={returnToRankMatchSetup}
                onSearchSuperseded={handleRankMatchSearchSuperseded}
                opponentCounts={rankMatchOpponentCounts}
              />
            )}
            {screen === SCREENS.LOBBY && enteredLobbyId && (
              <LobbyPage
                lobbyId={enteredLobbyId}
                onLobbyClosed={handleLobbyLeave}
                onGameStart={handleGameStart}
                onGoToCrystals={goToShopCrystals}
                onOpenInventory={() => setScreen(SCREENS.INVENTORY)}
              />
            )}
            {screen === SCREENS.INVENTORY && (
              <InventoryPage
                onBack={goToMainMenu}
                focusSection={inventoryFocusSection}
                onFactionClick={(factionId, selectedSkinId, availableFactions, availableSkinIds, focusUnitSkins) => {
                  setFactionPageContext({
                    factionId,
                    selectedSkinId,
                    availableFactions,
                    availableSkinIds,
                    focusUnitSkins: Boolean(focusUnitSkins),
                    returnScreen: SCREENS.INVENTORY,
                  });
                  setScreen(SCREENS.FACTION_PAGE);
                }}
                onAllSkinsClick={(factions, availableSkinIds) => {
                  setAllSkinsContext({returnScreen: SCREENS.INVENTORY, factions, availableSkinIds});
                  setScreen(SCREENS.ALL_SKINS);
                }}
              />
            )}
            {screen === SCREENS.USER_INVENTORY && userInventoryContext && (
              <InventoryPage
                userId={userInventoryContext.userId}
                userName={userInventoryContext.userName}
                onBack={() => setScreen(userInventoryContext.returnScreen || SCREENS.USER_PROFILE)}
                onFactionClick={(factionId, selectedSkinId, availableFactions, availableSkinIds, focusUnitSkins, collection) => {
                  setFactionPageContext({
                    factionId,
                    selectedSkinId,
                    availableFactions,
                    availableSkinIds,
                    focusUnitSkins: Boolean(focusUnitSkins),
                    returnScreen: SCREENS.USER_INVENTORY,
                    inventoryScreen: SCREENS.USER_INVENTORY,
                    isReadOnly: true,
                    userName: userInventoryContext.userName,
                    factionLoadouts: collection?.factionLoadouts,
                    unitSkinIds: collection?.unitSkinIds,
                  });
                  setScreen(SCREENS.FACTION_PAGE);
                }}
                onAllSkinsClick={(factions, availableSkinIds, factionLoadouts, unitSkinIds) => {
                  setAllSkinsContext({
                    returnScreen: SCREENS.USER_INVENTORY,
                    factions,
                    availableSkinIds,
                    factionLoadouts,
                    unitSkinIds,
                    userName: userInventoryContext.userName,
                    isReadOnly: true,
                  });
                  setScreen(SCREENS.ALL_SKINS);
                }}
              />
            )}
            {screen === SCREENS.PROFILE && (
              <AdminUserProfilePage
                isOwnProfile
                userId={auth.id}
                userName={auth.name}
                isSuperAdmin={auth.isSuperAdmin}
                onBack={goToMainMenu}
                onQuestsClick={() => setScreen(SCREENS.QUESTS)}
                onRatingCrystalsClick={() => {
                  setRatingCrystalsContext({returnScreen: SCREENS.PROFILE});
                  setScreen(SCREENS.RATING_CRYSTALS);
                }}
                onBuyPremiumClick={goToShopPremium}
                onInviteFriendClick={() => {
                  setFriendInviteContext({
                    userId: auth.id,
                    userName: auth.name,
                    isAdminView: false,
                    returnScreen: SCREENS.PROFILE,
                  });
                  setScreen(SCREENS.FRIEND_INVITE);
                }}
                onMatchClick={(matchId) => {
                  setSelectedMatchId(matchId);
                  setMatchDetailsContext({returnScreen: SCREENS.PROFILE});
                  setScreen(SCREENS.MATCH_DETAILS);
                }}
                onAllMatchesClick={() => {
                  setMatchHistoryContext({userId: auth.id, userName: auth.name, returnScreen: SCREENS.PROFILE});
                  setScreen(SCREENS.MATCH_HISTORY);
                }}
                onGrantAssetsClick={() => {
                  setGrantAssetsContext({userId: auth.id, userName: auth.name, returnScreen: SCREENS.PROFILE});
                  setScreen(SCREENS.GRANT_ASSETS);
                }}
                onChangeNicknameClick={() => {
                  setChangeNicknameContext({userId: auth.id, userName: auth.name, returnScreen: SCREENS.PROFILE});
                  setScreen(SCREENS.CHANGE_NICKNAME);
                }}
                onPurchaseHistoryClick={() => {
                  setPurchaseHistoryContext({userId: auth.id, userName: auth.name, returnScreen: SCREENS.PROFILE});
                  setScreen(SCREENS.PURCHASE_HISTORY);
                }}
                onChestOpeningHistoryClick={() => {
                  setChestOpeningHistoryContext({userId: auth.id, userName: auth.name, returnScreen: SCREENS.PROFILE});
                  setScreen(SCREENS.CHEST_OPENING_HISTORY);
                }}
                onDailyLoginRewardHistoryClick={() => {
                  setDailyLoginRewardHistoryContext({
                    userId: auth.id,
                    userName: auth.name,
                    returnScreen: SCREENS.PROFILE
                  });
                  setScreen(SCREENS.DAILY_LOGIN_REWARD_HISTORY);
                }}
              />
            )}
            {screen === SCREENS.MATCH_HISTORY && (
              <MatchHistoryPage
                isAllMatches={matchHistoryContext?.isAllMatches}
                userId={matchHistoryContext?.userId || auth.id}
                userName={matchHistoryContext?.userName || auth.name}
                onBack={() => setScreen(matchHistoryContext?.returnScreen || SCREENS.PROFILE)}
                onMatchClick={(matchId) => {
                  setSelectedMatchId(matchId);
                  setMatchDetailsContext({returnScreen: SCREENS.MATCH_HISTORY});
                  setScreen(SCREENS.MATCH_DETAILS);
                }}
              />
            )}
            {screen === SCREENS.PURCHASE_HISTORY && (
              <PurchaseHistoryPage
                isAllPurchases={purchaseHistoryContext?.isAllPurchases}
                userId={purchaseHistoryContext?.userId || auth.id}
                userName={purchaseHistoryContext?.userName || auth.name}
                onBack={() => setScreen(purchaseHistoryContext?.returnScreen || SCREENS.PROFILE)}
                onUserClick={(userId, userName) => {
                  setAdminSelectedUser({id: userId, name: userName});
                  setScreen(SCREENS.ADMIN_USER_PROFILE);
                }}
              />
            )}
            {screen === SCREENS.CHEST_OPENING_HISTORY && (
              <ChestOpeningHistoryPage
                isAllOpenings={chestOpeningHistoryContext?.isAllOpenings}
                userId={chestOpeningHistoryContext?.userId || auth.id}
                userName={chestOpeningHistoryContext?.userName || auth.name}
                onBack={() => setScreen(chestOpeningHistoryContext?.returnScreen || SCREENS.PROFILE)}
                onUserClick={(userId, userName) => {
                  setAdminSelectedUser({id: userId, name: userName});
                  setScreen(SCREENS.ADMIN_USER_PROFILE);
                }}
              />
            )}
            {screen === SCREENS.DAILY_LOGIN_REWARD_HISTORY && (
              <DailyLoginRewardHistoryPage
                isAllClaims={dailyLoginRewardHistoryContext?.isAllClaims}
                userId={dailyLoginRewardHistoryContext?.userId || auth.id}
                userName={dailyLoginRewardHistoryContext?.userName || auth.name}
                onBack={() => setScreen(dailyLoginRewardHistoryContext?.returnScreen || SCREENS.PROFILE)}
                onUserClick={(userId, userName) => {
                  setAdminSelectedUser({id: userId, name: userName});
                  setScreen(SCREENS.ADMIN_USER_PROFILE);
                }}
              />
            )}
            {screen === SCREENS.MATCH_DETAILS && selectedMatchId && (
              <MatchDetailsPage
                matchId={selectedMatchId}
                onBack={() => setScreen(matchDetailsContext?.returnScreen || SCREENS.MATCH_HISTORY)}
                onMainMenu={goToMainMenu}
                onPlayerClick={(userId, userName) => {
                  setGlorySelectedUser({id: userId, name: userName});
                  setUserProfileReturnScreen(SCREENS.MATCH_DETAILS);
                  setScreen(SCREENS.USER_PROFILE);
                }}
                onWatchReplay={handleWatchReplay}
              />
            )}
            {screen === SCREENS.TUTORIAL_MISSIONS && (
              <TutorialMissionsPage
                onStartChapter={startTutorialChapter}
                onOpenCampaign={(factionId) => {
                  setCampaignFactionId(factionId);
                  setScreen(SCREENS.CAMPAIGN_MISSIONS);
                }}
                onCreateMission={() => {
                  store.dispatch(resetState());
                  setIsMissionEditorMode(true);
                  setScreen(SCREENS.FIELD_SELECT);
                }}
                onBack={goToMainMenu}
              />
            )}
            {screen === SCREENS.RATING_CRYSTALS && (
              <RatingCrystalsPage
                userId={ratingCrystalsContext?.userId}
                userName={ratingCrystalsContext?.userName}
                onBack={() => setScreen(ratingCrystalsContext?.returnScreen || SCREENS.PROFILE)}
              />
            )}
            {screen === SCREENS.ADMIN_RATING_CRYSTALS && (
              <RatingCrystalsPage isAllUsers onBack={() => setScreen(SCREENS.ADMIN)}/>
            )}
            {screen === SCREENS.CAMPAIGN_MISSIONS && (
              <CampaignMissionsPage
                factionId={campaignFactionId}
                onStartMission={startCampaignMission}
                onBack={() => setScreen(SCREENS.TUTORIAL_MISSIONS)}
              />
            )}
            {screen === SCREENS.QUESTS && (
              <QuestsPage
                onStartChapter={startTutorialChapter}
                onLocalGame={() => setScreen(SCREENS.FIELD_SELECT)}
                onOnlineGame={() => setScreen(SCREENS.LOBBY_LIST)}
                onRankedGame={openRankMatchSetup}
                onShop={() => {
                  setShopInitialTab(null);
                  setScreen(SCREENS.SHOP);
                }}
                onBack={() => setScreen(SCREENS.PROFILE)}
              />
            )}
            {screen === SCREENS.GLORY_HALL && (
              <GloryHallPage
                onBack={goToMainMenu}
                onUserClick={(userId, userName) => openUserProfile(userId, userName, SCREENS.GLORY_HALL)}
              />
            )}
            {/* Клик по игроку открывает сразу его коллекцию, минуя профиль:
                раздел про коллекции. Своя плашка ведет туда же — коллекция в
                том виде, в каком ее видят другие, а «Назад» возвращает в
                список (из «Моей коллекции» возврат был бы в главное меню) */}
            {screen === SCREENS.BEST_COLLECTIONS && (
              <BestCollectionsPage
                onBack={goToMainMenu}
                onUserClick={(userId, userName) => {
                  setUserInventoryContext({userId, userName, returnScreen: SCREENS.BEST_COLLECTIONS});
                  setScreen(SCREENS.USER_INVENTORY);
                }}
              />
            )}
            {screen === SCREENS.USER_PROFILE && glorySelectedUser && (
              <AdminUserProfilePage
                isPublicView
                userId={glorySelectedUser.id}
                userName={glorySelectedUser.name}
                isSuperAdmin={auth.isSuperAdmin}
                onRatingCrystalsClick={() => {
                  setRatingCrystalsContext({
                    userId: glorySelectedUser.id,
                    userName: glorySelectedUser.name,
                    returnScreen: SCREENS.USER_PROFILE,
                  });
                  setScreen(SCREENS.RATING_CRYSTALS);
                }}
                onCollectionClick={() => {
                  setUserInventoryContext({
                    userId: glorySelectedUser.id,
                    userName: glorySelectedUser.name,
                    returnScreen: SCREENS.USER_PROFILE,
                  });
                  setScreen(SCREENS.USER_INVENTORY);
                }}
                onBack={() => setScreen(userProfileReturnScreen || SCREENS.GLORY_HALL)}
                onMainMenu={goToMainMenu}
                onMatchClick={(matchId) => {
                  setSelectedMatchId(matchId);
                  setMatchDetailsContext({returnScreen: SCREENS.USER_PROFILE});
                  setScreen(SCREENS.MATCH_DETAILS);
                }}
                onAllMatchesClick={() => {
                  setMatchHistoryContext({
                    userId: glorySelectedUser.id,
                    userName: glorySelectedUser.name,
                    returnScreen: SCREENS.USER_PROFILE,
                  });
                  setScreen(SCREENS.MATCH_HISTORY);
                }}
                onGrantAssetsClick={() => {
                  setGrantAssetsContext({
                    userId: glorySelectedUser.id,
                    userName: glorySelectedUser.name,
                    returnScreen: SCREENS.USER_PROFILE,
                  });
                  setScreen(SCREENS.GRANT_ASSETS);
                }}
                onChangeNicknameClick={() => {
                  setChangeNicknameContext({
                    userId: glorySelectedUser.id,
                    userName: glorySelectedUser.name,
                    returnScreen: SCREENS.USER_PROFILE,
                  });
                  setScreen(SCREENS.CHANGE_NICKNAME);
                }}
                onPurchaseHistoryClick={() => {
                  setPurchaseHistoryContext({
                    userId: glorySelectedUser.id,
                    userName: glorySelectedUser.name,
                    returnScreen: SCREENS.USER_PROFILE,
                  });
                  setScreen(SCREENS.PURCHASE_HISTORY);
                }}
                onChestOpeningHistoryClick={() => {
                  setChestOpeningHistoryContext({
                    userId: glorySelectedUser.id,
                    userName: glorySelectedUser.name,
                    returnScreen: SCREENS.USER_PROFILE,
                  });
                  setScreen(SCREENS.CHEST_OPENING_HISTORY);
                }}
                onDailyLoginRewardHistoryClick={() => {
                  setDailyLoginRewardHistoryContext({
                    userId: glorySelectedUser.id,
                    userName: glorySelectedUser.name,
                    returnScreen: SCREENS.USER_PROFILE,
                  });
                  setScreen(SCREENS.DAILY_LOGIN_REWARD_HISTORY);
                }}
                onPartnerClick={() => {
                  setPartnerFormContext({
                    userId: glorySelectedUser.id,
                    userName: glorySelectedUser.name,
                    returnScreen: SCREENS.USER_PROFILE,
                  });
                  setScreen(SCREENS.PARTNER_FORM);
                }}
                onInvitedFriendsClick={() => {
                  setFriendInviteContext({
                    userId: glorySelectedUser.id,
                    userName: glorySelectedUser.name,
                    referalCode: glorySelectedUser.friendReferalCode,
                    isAdminView: true,
                    returnScreen: SCREENS.USER_PROFILE,
                  });
                  setScreen(SCREENS.FRIEND_INVITE);
                }}
              />
            )}
            {screen === SCREENS.SETTINGS && (
              <SettingsPage
                onBack={goToMainMenu}
              />
            )}
            {screen === SCREENS.ACKNOWLEDGMENTS && (
              <AcknowledgmentsPage
                onBack={goToMainMenu}
              />
            )}
            {screen === SCREENS.LICENSE_AGREEMENT && (
              <LicenseAgreementPage
                onBack={goToMainMenu}
              />
            )}
            {screen === SCREENS.PRIVACY_POLICY && (
              <PrivacyPolicyPage
                onBack={goToMainMenu}
              />
            )}
            {screen === SCREENS.CONTACTS && (
              <ContactsPage
                onBack={goToMainMenu}
              />
            )}
            {screen === SCREENS.SHOP && (
              <ShopPage
                onBack={goToMainMenu}
                onOpenInventory={() => setScreen(SCREENS.INVENTORY)}
                initialTab={shopInitialTab}
              />
            )}
            {screen === SCREENS.ADMIN && (
              <AdminPage
                onUsersList={() => setScreen(SCREENS.ADMIN_USERS)}
                onDeleteUser={() => setScreen(SCREENS.ADMIN_DELETE_USER)}
                onModalsTest={() => setScreen(SCREENS.ADMIN_MODALS_TEST)}
                onGameUiKit={() => setScreen(SCREENS.ADMIN_GAME_UI_KIT)}
                onMatchesList={() => {
                  setMatchHistoryContext({isAllMatches: true, returnScreen: SCREENS.ADMIN});
                  setScreen(SCREENS.MATCH_HISTORY);
                }}
                onPurchasesList={() => {
                  setPurchaseHistoryContext({isAllPurchases: true, returnScreen: SCREENS.ADMIN});
                  setScreen(SCREENS.PURCHASE_HISTORY);
                }}
                onChestOpeningsList={() => {
                  setChestOpeningHistoryContext({isAllOpenings: true, returnScreen: SCREENS.ADMIN});
                  setScreen(SCREENS.CHEST_OPENING_HISTORY);
                }}
                onDailyLoginRewardsList={() => {
                  setDailyLoginRewardHistoryContext({isAllClaims: true, returnScreen: SCREENS.ADMIN});
                  setScreen(SCREENS.DAILY_LOGIN_REWARD_HISTORY);
                }}
                onRatingCrystalsList={() => setScreen(SCREENS.ADMIN_RATING_CRYSTALS)}
                onCreateRankedLobby={handleAdminCreateRankedLobby}
                onPartnersList={() => setScreen(SCREENS.ADMIN_PARTNERS)}
                onFriendReferalConfig={() => setScreen(SCREENS.ADMIN_FRIEND_REFERAL_CONFIG)}
                onLoginPromo={() => setScreen(SCREENS.ADMIN_LOGIN_PROMO)}
                onBack={goToMainMenu}
              />
            )}
            {screen === SCREENS.ADMIN_PARTNERS && (
              <AdminPartnersPage
                onPartnerClick={(id, name) => {
                  setAdminSelectedUser({id, name});
                  setScreen(SCREENS.ADMIN_USER_PROFILE);
                }}
                onBack={() => setScreen(SCREENS.ADMIN)}
              />
            )}
            {screen === SCREENS.ADMIN_FRIEND_REFERAL_CONFIG && (
              <AdminFriendReferalConfigPage
                onBack={() => setScreen(SCREENS.ADMIN)}
              />
            )}
            {screen === SCREENS.ADMIN_LOGIN_PROMO && (
              <AdminLoginPromoPage
                onBack={() => setScreen(SCREENS.ADMIN)}
              />
            )}
            {screen === SCREENS.FRIEND_INVITE && friendInviteContext && (
              <FriendInvitePage
                targetUserId={friendInviteContext.userId}
                targetUserName={friendInviteContext.userName}
                targetReferalCode={friendInviteContext.referalCode}
                isAdminView={Boolean(friendInviteContext.isAdminView)}
                onUserClick={(userId, userName) => openUserProfile(userId, userName, SCREENS.FRIEND_INVITE)}
                onBack={() => {
                  // Возврат в профиль владельца списка: клик по плашке друга
                  // мог перезаписать выбранного игрока его профилем, а чей
                  // это список — помнит контекст
                  if (friendInviteContext.returnScreen === SCREENS.USER_PROFILE) {
                    setGlorySelectedUser({
                      id: friendInviteContext.userId,
                      name: friendInviteContext.userName,
                      friendReferalCode: friendInviteContext.referalCode,
                    });
                  }
                  setScreen(friendInviteContext.returnScreen || SCREENS.PROFILE);
                }}
              />
            )}
            {screen === SCREENS.ADMIN_MODALS_TEST && (
              <AdminModalsTestPage onBack={() => setScreen(SCREENS.ADMIN)}/>
            )}
            {screen === SCREENS.ADMIN_GAME_UI_KIT && (
              <AdminGameUiKitPage onBack={() => setScreen(SCREENS.ADMIN)}/>
            )}
            {screen === SCREENS.ADMIN_USERS && (
              <AdminUsersPage
                onUserClick={(id, name) => {
                  setAdminSelectedUser({id, name});
                  setScreen(SCREENS.ADMIN_USER_PROFILE);
                }}
                onBack={() => setScreen(SCREENS.ADMIN)}
              />
            )}
            {screen === SCREENS.ADMIN_USER_PROFILE && adminSelectedUser && (
              <AdminUserProfilePage
                userId={adminSelectedUser.id}
                userName={adminSelectedUser.name}
                isSuperAdmin={auth.isSuperAdmin}
                onRatingCrystalsClick={() => {
                  setRatingCrystalsContext({
                    userId: adminSelectedUser.id,
                    userName: adminSelectedUser.name,
                    returnScreen: SCREENS.ADMIN_USER_PROFILE,
                  });
                  setScreen(SCREENS.RATING_CRYSTALS);
                }}
                onCollectionClick={() => {
                  setUserInventoryContext({
                    userId: adminSelectedUser.id,
                    userName: adminSelectedUser.name,
                    returnScreen: SCREENS.ADMIN_USER_PROFILE,
                  });
                  setScreen(SCREENS.USER_INVENTORY);
                }}
                onBack={() => setScreen(SCREENS.ADMIN_USERS)}
                onMainMenu={goToMainMenu}
                onMatchClick={(matchId) => {
                  setSelectedMatchId(matchId);
                  setMatchDetailsContext({returnScreen: SCREENS.ADMIN_USER_PROFILE});
                  setScreen(SCREENS.MATCH_DETAILS);
                }}
                onAllMatchesClick={() => {
                  setMatchHistoryContext({
                    userId: adminSelectedUser.id,
                    userName: adminSelectedUser.name,
                    returnScreen: SCREENS.ADMIN_USER_PROFILE,
                  });
                  setScreen(SCREENS.MATCH_HISTORY);
                }}
                onGrantAssetsClick={() => {
                  setGrantAssetsContext({
                    userId: adminSelectedUser.id,
                    userName: adminSelectedUser.name,
                    returnScreen: SCREENS.ADMIN_USER_PROFILE,
                  });
                  setScreen(SCREENS.GRANT_ASSETS);
                }}
                onChangeNicknameClick={() => {
                  setChangeNicknameContext({
                    userId: adminSelectedUser.id,
                    userName: adminSelectedUser.name,
                    returnScreen: SCREENS.ADMIN_USER_PROFILE,
                  });
                  setScreen(SCREENS.CHANGE_NICKNAME);
                }}
                onPurchaseHistoryClick={() => {
                  setPurchaseHistoryContext({
                    userId: adminSelectedUser.id,
                    userName: adminSelectedUser.name,
                    returnScreen: SCREENS.ADMIN_USER_PROFILE,
                  });
                  setScreen(SCREENS.PURCHASE_HISTORY);
                }}
                onChestOpeningHistoryClick={() => {
                  setChestOpeningHistoryContext({
                    userId: adminSelectedUser.id,
                    userName: adminSelectedUser.name,
                    returnScreen: SCREENS.ADMIN_USER_PROFILE,
                  });
                  setScreen(SCREENS.CHEST_OPENING_HISTORY);
                }}
                onDailyLoginRewardHistoryClick={() => {
                  setDailyLoginRewardHistoryContext({
                    userId: adminSelectedUser.id,
                    userName: adminSelectedUser.name,
                    returnScreen: SCREENS.ADMIN_USER_PROFILE,
                  });
                  setScreen(SCREENS.DAILY_LOGIN_REWARD_HISTORY);
                }}
                onPartnerClick={() => {
                  setPartnerFormContext({
                    userId: adminSelectedUser.id,
                    userName: adminSelectedUser.name,
                    returnScreen: SCREENS.ADMIN_USER_PROFILE,
                  });
                  setScreen(SCREENS.PARTNER_FORM);
                }}
                onInvitedFriendsClick={() => {
                  setFriendInviteContext({
                    userId: adminSelectedUser.id,
                    userName: adminSelectedUser.name,
                    referalCode: adminSelectedUser.friendReferalCode,
                    isAdminView: true,
                    returnScreen: SCREENS.ADMIN_USER_PROFILE,
                  });
                  setScreen(SCREENS.FRIEND_INVITE);
                }}
              />
            )}
            {screen === SCREENS.ADMIN_DELETE_USER && (
              <AdminDeleteUserPage
                onBack={() => setScreen(SCREENS.ADMIN)}
              />
            )}
            {screen === SCREENS.GRANT_ASSETS && grantAssetsContext && (
              <GrantAssetsPage
                targetUserName={grantAssetsContext.userName}
                onBack={() => setScreen(grantAssetsContext.returnScreen || SCREENS.PROFILE)}
                onGrantChest={() => {
                  setGrantChestContext(grantAssetsContext);
                  setScreen(SCREENS.GRANT_CHEST);
                }}
                onGrantSkin={() => {
                  setGrantSkinContext(grantAssetsContext);
                  setScreen(SCREENS.GRANT_SKIN);
                }}
                onGrantCrystals={() => {
                  setGrantCrystalsContext(grantAssetsContext);
                  setScreen(SCREENS.GRANT_CRYSTALS);
                }}
                onGrantPremium={() => {
                  setGrantPremiumContext(grantAssetsContext);
                  setScreen(SCREENS.GRANT_PREMIUM);
                }}
              />
            )}
            {screen === SCREENS.GRANT_CHEST && grantChestContext && (
              <GrantChestPage
                targetUserId={grantChestContext.userId}
                targetUserName={grantChestContext.userName}
                onBack={() => setScreen(SCREENS.GRANT_ASSETS)}
              />
            )}
            {screen === SCREENS.CHANGE_NICKNAME && changeNicknameContext && (
              <ChangeNicknamePage
                targetUserId={changeNicknameContext.userId}
                targetUserName={changeNicknameContext.userName}
                onBack={() => setScreen(changeNicknameContext.returnScreen || SCREENS.PROFILE)}
                onRenamed={(newName) => {
                  const renamedUserId = changeNicknameContext.userId;
                  // Подзаголовок страницы и профиль, из которого пришли, должны
                  // показывать новое имя сразу, без перезагрузки
                  setChangeNicknameContext((context) => ({...context, userName: newName}));
                  setAdminSelectedUser((user) => (
                    user && user.id === renamedUserId ? {...user, name: newName} : user
                  ));
                  setGlorySelectedUser((user) => (
                    user && user.id === renamedUserId ? {...user, name: newName} : user
                  ));
                  // Переименовали САМОГО СЕБЯ: имя игрока в НОВЫЕ матчи уходит
                  // из клиентского состояния (см. playerName в заявках на
                  // матч ниже) и из localStorage — не обновив их, в лобби
                  // продолжил бы попадать старый ник. Куку сервер переподписал
                  // сам. Чужому клиенту так не дотянуться: там ник обновится
                  // при следующем входе
                  if (renamedUserId === auth.id) {
                    dispatch(setPlayerAuth({...auth, name: newName}));
                    localStorage.setItem('playerName', newName);
                  }
                }}
              />
            )}
            {screen === SCREENS.GRANT_SKIN && grantSkinContext && (
              <GrantSkinPage
                targetUserId={grantSkinContext.userId}
                targetUserName={grantSkinContext.userName}
                onBack={() => setScreen(SCREENS.GRANT_ASSETS)}
              />
            )}
            {screen === SCREENS.GRANT_CRYSTALS && grantCrystalsContext && (
              <GrantCrystalsPage
                targetUserId={grantCrystalsContext.userId}
                targetUserName={grantCrystalsContext.userName}
                onBack={() => setScreen(SCREENS.GRANT_ASSETS)}
              />
            )}
            {screen === SCREENS.GRANT_PREMIUM && grantPremiumContext && (
              <GrantPremiumPage
                targetUserId={grantPremiumContext.userId}
                targetUserName={grantPremiumContext.userName}
                onBack={() => setScreen(SCREENS.GRANT_ASSETS)}
              />
            )}
            {screen === SCREENS.PARTNER_FORM && partnerFormContext && (
              <PartnerFormPage
                targetUserId={partnerFormContext.userId}
                targetUserName={partnerFormContext.userName}
                isSelfView={Boolean(partnerFormContext.isSelfView)}
                onBack={() => setScreen(partnerFormContext.returnScreen || SCREENS.MENU)}
                onRegistrationsClick={() => {
                  setPartnerRegistrationsContext({
                    userId: partnerFormContext.userId,
                    userName: partnerFormContext.userName,
                    isSelfView: Boolean(partnerFormContext.isSelfView),
                  });
                  setScreen(SCREENS.PARTNER_REGISTRATIONS);
                }}
              />
            )}
            {screen === SCREENS.PARTNER_REGISTRATIONS && partnerRegistrationsContext && (
              <PartnerRegistrationsPage
                targetUserId={partnerRegistrationsContext.userId}
                targetUserName={partnerRegistrationsContext.userName}
                isSelfView={Boolean(partnerRegistrationsContext.isSelfView)}
                onUserClick={(userId, userName) =>
                  openUserProfile(userId, userName, SCREENS.PARTNER_REGISTRATIONS)}
                onBack={() => setScreen(SCREENS.PARTNER_FORM)}
              />
            )}
            {screen === SCREENS.ALL_SKINS && (
              <AllSkinsPage
                initialFactions={allSkinsContext?.factions}
                initialAvailableSkinIds={allSkinsContext?.availableSkinIds}
                initialFactionLoadouts={allSkinsContext?.factionLoadouts}
                initialUnitSkinIds={allSkinsContext?.unitSkinIds}
                userName={allSkinsContext?.userName}
                onBack={() => setScreen(allSkinsContext?.returnScreen || SCREENS.INVENTORY)}
                onBackToInventory={() => setScreen(allSkinsContext?.returnScreen === SCREENS.USER_INVENTORY
                  ? SCREENS.USER_INVENTORY
                  : SCREENS.INVENTORY)}
                onFactionClick={(factionId, selectedSkinId, availableFactions, availableSkinIds, focusUnitSkins, collection) => {
                  const isReadOnly = Boolean(allSkinsContext?.isReadOnly);
                  setFactionPageContext({
                    factionId,
                    selectedSkinId,
                    availableFactions,
                    availableSkinIds,
                    focusUnitSkins: Boolean(focusUnitSkins),
                    returnScreen: SCREENS.ALL_SKINS,
                    inventoryScreen: isReadOnly ? SCREENS.USER_INVENTORY : SCREENS.INVENTORY,
                    isReadOnly,
                    userName: allSkinsContext?.userName,
                    factionLoadouts: isReadOnly ? collection?.factionLoadouts : undefined,
                    unitSkinIds: isReadOnly ? collection?.unitSkinIds : undefined,
                  });
                  setScreen(SCREENS.FACTION_PAGE);
                }}
              />
            )}
            {screen === SCREENS.FACTION_PAGE && factionPageContext && (
              <FactionPage
                factionId={factionPageContext.factionId}
                selectedSkinId={factionPageContext.selectedSkinId}
                availableFactions={factionPageContext.availableFactions}
                availableSkinIds={factionPageContext.availableSkinIds}
                focusUnitSkins={factionPageContext.focusUnitSkins}
                isReadOnly={factionPageContext.isReadOnly}
                userName={factionPageContext.userName}
                factionLoadouts={factionPageContext.factionLoadouts}
                ownedUnitSkinIds={factionPageContext.unitSkinIds}
                onBack={() => setScreen(factionPageContext.returnScreen || SCREENS.INVENTORY)}
                onSkinSelected={() => setScreen(factionPageContext.returnScreen || SCREENS.INVENTORY)}
                onBackToInventory={() => setScreen(factionPageContext.inventoryScreen || SCREENS.INVENTORY)}
                onGoToCrystals={goToShopCrystals}
              />
            )}
          </div>
        ) : <GameContent
          key={tutorialChapterId || campaignMissionId || 'default_game'}
          field={selectedField}
          roundCount={gameRoundCount}
          players={players}
          isSpectatorMode={isSpectatorGame}
          isLocalTimerEnabled={isLocalTimerEnabled}
          chosenGroundBackground={localGroundBackground}
          isMissionEditorMode={isMissionEditorMode}
          setScreen={setScreen}
          handleRankedGame={handleRankedGame}
          replayActions={replayActions}
          replayFallbackResults={replayFallbackResults}
          isTutorialReplay={isTutorialReplay}
          tutorialChapterId={tutorialChapterId}
          onTutorialNextChapter={startTutorialChapter}
          onTutorialOpenInventory={() => exitTutorialToScreen(SCREENS.INVENTORY)}
          onTutorialExitToMenu={() => exitTutorialToScreen(SCREENS.MENU)}
          onNewbieBattle={startNewbieBattle}
          missionId={campaignMissionId}
          onMissionRestart={startCampaignMission}
          onMissionOpenInventory={() => exitMissionToScreen(SCREENS.INVENTORY)}
          onMissionExitToMenu={() => exitMissionToScreen(SCREENS.MENU)}
        />
      )}
      <RulesModal/>
      {notEnoughFactionsModal && (
        <NotEnoughFactionsModal
          availableCount={notEnoughFactionsModal.availableCount}
          onGoToQuests={() => {
            setNotEnoughFactionsModal(null);
            setScreen(SCREENS.QUESTS);
          }}
          onGoToShop={() => {
            setNotEnoughFactionsModal(null);
            // Сразу вкладка фракций: модалка про них (и про бесплатную по акции)
            setShopInitialTab(SHOP_TABS.FACTIONS);
            setScreen(SCREENS.SHOP);
          }}
          onHide={() => setNotEnoughFactionsModal(null)}
        />
      )}
      {lobbyIsFullModal && (
        <LobbyIsFullModal
          maxPlayers={lobbyIsFullModal.maxPlayers}
          onHide={() => setLobbyIsFullModal(null)}
        />
      )}
      {rankMatchSearchNotice && (
        <RankMatchSearchNoticeModal
          notice={rankMatchSearchNotice}
          onHide={() => setRankMatchSearchNotice(null)}
        />
      )}
      {aiLobbyJoinErrorText && (
        <AiMatchJoinErrorModal
          text={aiLobbyJoinErrorText}
          onHide={() => setAiLobbyJoinErrorText(null)}
        />
      )}
      {isReplayUnavailableModalVisible && (
        <MatchReplayUnavailableModal
          isAccessDenied={isReplayAccessDenied}
          onHide={() => setIsReplayUnavailableModalVisible(false)}
        />
      )}
      <OrientationLockOverlay/>
      {(connectionLost || reconnectStatus || sessionSuperseded) && (
        <ConnectionLostOverlay
          reconnectStatus={(connectionLost || sessionSuperseded) ? null : reconnectStatus}
          isSuperseded={sessionSuperseded}
        />
      )}
      {versionMismatch && <VersionMismatchOverlay serverVersion={versionMismatchServerVersion}/>}
      {gameEndedByHost && (
        <GameEndedByHostOverlay
          onMainMenu={() => {
            closeNetworkSocket();
            store.dispatch({...resetState(), _fromServer: true});
            setScreen(SCREENS.MENU);
            setGameEndedByHost(false);
          }}
        />
      )}
    </>
  );
}

export default App;
